import React from 'react';

type MetricsWorkspaceTab = 'overview' | 'service' | 'reliability' | 'customers' | 'sla' | 'inventory' | 'team';

type SignalTone = 'danger' | 'warning' | 'info';

type MetricsRow = {
  key?: string;
  id?: string;
  caseId?: string;
  label?: string;
  name?: string;
  count?: number;
  totalCases?: number;
  openCases?: number;
  agedOpen?: number;
  pendingSurveys?: number;
  caseCount?: number;
  taskCount?: number;
  failureCount?: number;
  mtbfDays?: number | null;
  timeToFirstFailureDays?: number | null;
  latestUpdateAt?: string | null;
  customer?: string;
  customerName?: string;
  technician?: string;
  surveys?: number;
  average?: number | null;
  assignee?: string;
  tasks?: number;
  scheduled?: number;
  pendingSchedule?: number;
  taskGroup?: string;
  caseCode?: string;
  assigneeLabel?: string;
  day?: string;
  assignees?: number;
  value?: string | number;
  meta?: string;
};

type ServiceCaseDrilldownRow = {
  id: string;
  caseId: string;
  caseCode: string;
  customerName: string;
  statusKey?: string;
  statusLabel: string;
  ageDays?: number | null;
  activeTasks: number;
  taskGroups?: string[];
  assigneeLabel?: string;
  scheduleState: string;
  quoteState: string;
  partsState: string;
  partsDetail?: string;
  partsEtaAt?: string | null;
  hasPartsIssue?: boolean;
  nextPlannedAt?: string | null;
  slaLabel?: string;
};

type ReliabilityCaseDrilldownRow = {
  id: string;
  caseId: string;
  caseCode: string;
  customerName: string;
  statusKey?: string;
  statusLabel: string;
  modelLabel?: string;
  deviceLabel?: string;
  openedAt?: string | null;
  closedAt?: string | null;
  durationDays?: number | null;
  activeTasks: number;
  repairTasks: number;
  taskGroups?: string[];
  assigneeLabel?: string;
  warrantyStatus?: string;
};

type CustomerCaseDrilldownRow = {
  id: string;
  caseId: string;
  caseCode: string;
  customerName: string;
  statusKey?: string;
  statusLabel: string;
  ageDays?: number | null;
  activeTasks: number;
  scheduledTasks: number;
  scheduleState: string;
  quoteState: string;
  taskGroups?: string[];
  assigneeLabel?: string;
  openedAt?: string | null;
  closedAt?: string | null;
  lastActivityAt?: string | null;
};

type SlaCaseDrilldownRow = {
  id: string;
  caseId: string;
  caseCode: string;
  customerName: string;
  statusKey?: string;
  statusLabel: string;
  modelLabel?: string;
  deviceLabel?: string;
  slaLabel?: string;
  openedAt?: string | null;
  closedAt?: string | null;
  lastActivityAt?: string | null;
  activeTasks: number;
  scheduledTasks: number;
  maintenanceTasks: number;
  repairTasks: number;
  taskGroups?: string[];
  assigneeLabel?: string;
  scheduleState: string;
  nextPlannedAt?: string | null;
};

type InventoryCaseDrilldownRow = {
  id: string;
  caseId: string;
  caseCode: string;
  customerName: string;
  statusKey?: string;
  statusLabel: string;
  deviceLabel?: string;
  shortageQty: number;
  etaAt?: string | null;
  detail: string;
  activeTasks: number;
  taskGroups?: string[];
  assigneeLabel?: string;
  planStatus?: string;
};

type TeamCaseDrilldownRow = {
  id: string;
  caseId: string;
  caseCode: string;
  customerName: string;
  statusKey?: string;
  statusLabel: string;
  modelLabel?: string;
  deviceLabel?: string;
  openedAt?: string | null;
  closedAt?: string | null;
  lastActivityAt?: string | null;
  activeTasks: number;
  completedTasks: number;
  scheduledTasks: number;
  pendingSchedule: number;
  taskGroups?: string[];
  assigneeLabel?: string;
};

const escapeCsvValue = (value: unknown): string => {
  const normalized = Array.isArray(value)
    ? value.join('; ')
    : value == null
    ? ''
    : String(value);
  const escaped = normalized.replace(/"/g, '""');
  return /[",\r\n]/.test(escaped) ? `"${escaped}"` : escaped;
};

const exportInsightsRowsToCsv = (
  filename: string,
  columns: Array<{ header: string; value: (row: any) => unknown }>,
  rows: any[]
) => {
  if (!Array.isArray(rows) || rows.length === 0 || typeof document === 'undefined') return;
  const csv = [
    columns.map(column => escapeCsvValue(column.header)).join(','),
    ...rows.map(row => columns.map(column => escapeCsvValue(column.value(row))).join(','))
  ].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

type Props = {
  styles: any;
  metricsWorkspaceTab: MetricsWorkspaceTab;
  isMetricsWidgetEnabled: (widgetId: string) => boolean;
  isMetricsWorkspaceBlockEnabled: (blockId: string) => boolean;
  insightSignals: Array<{ id: string; tone: SignalTone; title: string; detail: string }>;
  getInsightsSignalStyle: (tone: SignalTone) => React.CSSProperties;
  metricsWorkspaceKpis: Record<
    MetricsWorkspaceTab,
    Array<{ label: string; value: string | number; meta: string }>
  >;
  taskGroupRows: Array<{ label: string; count: number }>;
  ageBucketRows: Array<{ label: string; count: number }>;
  completedTaskGroupRows: Array<{ label: string; count: number }>;
  metricsGroupBy: 'model' | 'device';
  metricsData: {
    summary?: { warrantyIn?: number; warrantyOut?: number };
    tasksByDevice?: Array<{ key: string; label: string; caseCount: number; taskCount: number }>;
    mtbf?: Array<{
      key: string;
      label: string;
      failureCount: number;
      mtbfDays: number | null;
      timeToFirstFailureDays: number | null;
    }>;
  };
  customerLoadRows: Array<{
    id: string;
    customer: string;
    totalCases: number;
    taskCount: number;
    openCases: number;
  }>;
  serviceSummary?: {
    plannedTasks: number;
    pendingSchedule: number;
    unassignedTasks: number;
  } | null;
  statusDistributionRows: Array<{ label: string; count: number }>;
  scheduledTaskRows: MetricsRow[];
  pendingScheduleRows: MetricsRow[];
  unassignedTaskRows: MetricsRow[];
  scheduledThisWeekRows: MetricsRow[];
  upcomingWorkRows: MetricsRow[];
  serviceInsightQuotesLoading: boolean;
  serviceQuoteStatusRows: Array<{ label: string; count: number }>;
  partsWaitingRows: Array<{
    id: string;
    caseId?: string;
    caseCode: string;
    customerName: string;
    statusLabel: string;
    etaAt: string | null;
    detail: string;
  }>;
  upcomingSlaRows: Array<MetricsRow & { slaLabel: string }>;
  serviceCaseDrilldownRows: ServiceCaseDrilldownRow[];
  reliabilityCaseDrilldownRows: ReliabilityCaseDrilldownRow[];
  customerCaseDrilldownRows: CustomerCaseDrilldownRow[];
  slaCaseDrilldownRows: SlaCaseDrilldownRow[];
  inventoryCaseDrilldownRows: InventoryCaseDrilldownRow[];
  teamCaseDrilldownRows: TeamCaseDrilldownRow[];
  onOpenInsightsCase?: (caseId: string) => void | Promise<void>;
  slaInsightsLoading: boolean;
  slaCoverageRows: Array<{
    key: string;
    label: string;
    devices: number;
    customers: Set<string>;
    templateLabels: Set<string>;
  }>;
  expiringSlaRows: Array<{
    id: string;
    deviceLabel: string;
    customerName: string;
    slaLabel: string;
    endAt: Date;
    daysRemaining: number;
  }>;
  slaLifecycleSummary: {
    newCount: number;
    renewedCount: number;
    expiredCount: number;
  };
  slaCoveredCustomerWorkRows: Array<{
    customer: string;
    cases: number;
    tasks: number;
    scheduled: number;
  }>;
  scheduledSlaRows: Array<MetricsRow & { slaLabel: string }>;
  slaFirstTimeFixSummary: {
    eligible: number;
    fixedFirstTime: number;
    rate: number | null;
  };
  slaMtbfRows: Array<{
    key: string;
    label: string;
    repairs: number;
    deviceCount: number;
    mtbfDays: number | null;
  }>;
  slaMaintenanceComplianceSummary: {
    devicesDue: number;
    compliantDevices: number;
    rate: number | null;
  };
  slaMaintenanceDeviationRows: Array<{
    key: string;
    label: string;
    averageDeviationDays: number | null;
    onTimeRate: number | null;
    completed: number;
  }>;
  breakdownAfterMaintenanceRows: Array<{
    key: string;
    label: string;
    breakdowns: number;
    devices: number;
    averageDaysAfterMaintenance: number | null;
  }>;
  inventoryInsightsLoading: boolean;
  inventoryWarehouseValueRows: Array<{
    warehouseId: string;
    warehouseName: string;
    warehouseType: string;
    userLabel?: string | null;
    skuCount: number;
    quantityOnHand: number;
    quantityInTransit: number;
    quantityOnOrder: number;
    onHandValue: number;
    inTransitValue: number;
    onOrderValue: number;
  }>;
  inventoryTransitRows: Array<{
    warehouseId: string;
    warehouseName: string;
    quantityInTransit: number;
    quantityOnOrder: number;
    inTransitValue: number;
    onOrderValue: number;
  }>;
  inventoryVarianceCategoryRows: Array<{
    label: string;
    sessionCount: number;
    varianceLineCount: number;
    varianceValue: number;
  }>;
  inventoryCountSessionRows: Array<{
    id: string;
    warehouseName: string;
    warehouseType: string;
    countType: string;
    userLabel?: string | null;
    lineCount: number;
    varianceLineCount: number;
    varianceValue: number;
    approvedAt?: string | null;
  }>;
  inventoryVanStockRows: Array<{
    warehouseId: string;
    warehouseName: string;
    userLabel?: string | null;
    skuCount: number;
    quantityOnHand: number;
    onHandValue: number;
  }>;
  inventoryDelayedPartRows: Array<{
    id: string;
    caseId: string;
    caseCode: string;
    customerName: string;
    deviceLabel: string;
    shortageQty: number;
    etaAt?: string | null;
    detail: string;
  }>;
  reliabilityInsightsLoading: boolean;
  deviceRepairMtbfRows: Array<{
    key: string;
    label: string;
    failures: number;
    deviceCount: number;
    mtbfDays: number | null;
  }>;
  userRepairMtbfRows: Array<{
    technician: string;
    mtbfDays: number | null;
    repeatRepairs: number;
    repairedCases: number;
  }>;
  repairDurationRows: Array<{
    key: string;
    label: string;
    cases: number;
    averageDays: number | null;
  }>;
  reliabilityHoldReasonRows: Array<{ label: string; count: number }>;
  repairWarrantySummary: { total: number; inWarranty: number; outWarranty: number };
  maintenanceComplianceSummary: { devicesDue: number; compliantDevices: number; rate: number | null };
  commonFailureModeRows: Array<{ label: string; count: number }>;
  commonPartUsageRows: Array<{ label: string; count: number; quantity: number }>;
  repairFrequencyRows: Array<{
    key: string;
    label: string;
    repairs: number;
    installedBase: number;
    affectedDevices: number;
    percent: number | null;
  }>;
  formatInsightsDate: (value?: string | Date | null) => string;
  formatInsightsDateTime: (value?: string | Date | null) => string;
  repeatFailureRows: Array<{
    key: string;
    label: string;
    failureCount: number;
    mtbfDays: number | null;
  }>;
  customerSurveyRows: Array<{ customer: string; surveys: number; average: number | null }>;
  customerScheduledRows: Array<{
    customer: string;
    jobs: number;
    cases: number;
    nextPlannedAt: Date | null;
  }>;
  customerCompletedWorkRows: Array<{
    customer: string;
    tasks: number;
    cases: number;
    latestCompletedAt: Date | null;
  }>;
  customerStageTimingRows: Array<{
    key: string;
    label: string;
    averageDays: number | null;
    sampleCount: number;
  }>;
  userTaskHoursRows: Array<{
    technician: string;
    totalHours: number;
    taskCount: number;
    averageHours: number | null;
  }>;
  scheduledUserRows: Array<{
    technician: string;
    tasks: number;
    customers: number;
    nextPlannedAt: Date | null;
  }>;
  loadByAssigneeRows: Array<{
    assignee: string;
    tasks: number;
    scheduled: number;
    pendingSchedule: number;
  }>;
  scheduleDayRows: Array<{ day: string; tasks: number; assignees: number }>;
  surveyByTechnicianRows: Array<{ technician: string; surveys: number; average: number | null }>;
  userFirstTimeFixRows: Array<{
    technician: string;
    eligible: number;
    fixed: number;
    rate: number | null;
  }>;
  teamRepairPerformanceRows: Array<{
    technician: string;
    focusLabel: string;
    repairs: number;
    mtbfDays: number | null;
    firstTimeFixRate: number | null;
    eligible: number;
  }>;
  teamWorkloadDetailRows: Array<{
    technician: string;
    backlog: number;
    scheduled: number;
    pendingSchedule: number;
    hours: number;
  }>;
};

const formatDurationDays = (days?: number | null) => {
  if (typeof days !== 'number' || !Number.isFinite(days)) return '-';
  if (days < 1) {
    return `${Math.max(1, Math.round(days * 24))}h`;
  }
  return `${days.toFixed(1)}d`;
};

const formatHours = (hours?: number | null) => {
  if (typeof hours !== 'number' || !Number.isFinite(hours)) return '-';
  return `${hours.toFixed(1)}h`;
};

const formatSignedDurationDays = (days?: number | null) => {
  if (typeof days !== 'number' || !Number.isFinite(days)) return '-';
  const sign = days > 0 ? '+' : '';
  if (Math.abs(days) < 1) {
    const hours = Math.max(1, Math.round(Math.abs(days) * 24));
    return `${sign}${days < 0 ? '-' : ''}${hours}h`;
  }
  return `${sign}${days.toFixed(1)}d`;
};

const formatCurrencyValue = (value?: number | null) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value);
};

const formatQuantityValue = (value?: number | null) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
};

export const TechDashboardInsightsTab = React.memo(function TechDashboardInsightsTab({
  styles,
  metricsWorkspaceTab,
  isMetricsWidgetEnabled,
  isMetricsWorkspaceBlockEnabled,
  insightSignals,
  getInsightsSignalStyle,
  metricsWorkspaceKpis,
  taskGroupRows,
  ageBucketRows,
  completedTaskGroupRows,
  metricsGroupBy,
  metricsData,
  customerLoadRows,
  serviceSummary,
  statusDistributionRows,
  scheduledTaskRows,
  pendingScheduleRows,
  unassignedTaskRows,
  scheduledThisWeekRows,
  upcomingWorkRows,
  serviceInsightQuotesLoading,
  serviceQuoteStatusRows,
  partsWaitingRows,
  upcomingSlaRows,
  serviceCaseDrilldownRows,
  reliabilityCaseDrilldownRows,
  customerCaseDrilldownRows,
  slaCaseDrilldownRows,
  inventoryCaseDrilldownRows,
  teamCaseDrilldownRows,
  onOpenInsightsCase,
  slaInsightsLoading,
  slaCoverageRows,
  expiringSlaRows,
  slaLifecycleSummary,
  slaCoveredCustomerWorkRows,
  scheduledSlaRows,
  slaFirstTimeFixSummary,
  slaMtbfRows,
  slaMaintenanceComplianceSummary,
  slaMaintenanceDeviationRows,
  breakdownAfterMaintenanceRows,
  inventoryInsightsLoading,
  inventoryWarehouseValueRows,
  inventoryTransitRows,
  inventoryVarianceCategoryRows,
  inventoryCountSessionRows,
  inventoryVanStockRows,
  inventoryDelayedPartRows,
  reliabilityInsightsLoading,
  deviceRepairMtbfRows,
  userRepairMtbfRows,
  repairDurationRows,
  reliabilityHoldReasonRows,
  repairWarrantySummary,
  maintenanceComplianceSummary,
  commonFailureModeRows,
  commonPartUsageRows,
  repairFrequencyRows,
  formatInsightsDate,
  formatInsightsDateTime,
  repeatFailureRows,
  customerSurveyRows,
  customerScheduledRows,
  customerCompletedWorkRows,
  customerStageTimingRows,
  userTaskHoursRows,
  scheduledUserRows,
  loadByAssigneeRows,
  scheduleDayRows,
  surveyByTechnicianRows,
  userFirstTimeFixRows,
  teamRepairPerformanceRows,
  teamWorkloadDetailRows: serverTeamWorkloadDetailRows
}: Props) {
  const openServiceCase = React.useCallback(
    (caseId?: string | null) => {
      const resolvedCaseId = String(caseId || '').trim();
      if (!resolvedCaseId || !onOpenInsightsCase) return;
      void onOpenInsightsCase(resolvedCaseId);
    },
    [onOpenInsightsCase]
  );

  const serviceDrilldownButtonStyle: React.CSSProperties = {
    border: '1px solid #d9d1c4',
    background: '#fffaf2',
    color: '#083f3f',
    borderRadius: '999px',
    padding: '0.35rem 0.75rem',
    fontWeight: 700,
    cursor: 'pointer'
  };
  const exportButtonStyle: React.CSSProperties = {
    ...serviceDrilldownButtonStyle,
    borderRadius: '0.55rem',
    background: '#ffffff',
    padding: '0.32rem 0.7rem'
  };

  const exportServiceContributingCases = React.useCallback(() => {
    exportInsightsRowsToCsv(
      'service-delivery-contributing-cases.csv',
      [
        { header: 'Case', value: row => row.caseCode },
        { header: 'Customer', value: row => row.customerName },
        { header: 'Status', value: row => row.statusLabel },
        { header: 'Age days', value: row => row.ageDays },
        { header: 'Schedule', value: row => row.scheduleState },
        { header: 'Active tasks', value: row => row.activeTasks },
        { header: 'Quote state', value: row => row.quoteState },
        { header: 'Parts state', value: row => row.partsState },
        { header: 'Parts detail', value: row => row.partsDetail },
        { header: 'Owner', value: row => row.assigneeLabel || 'Unassigned' },
        { header: 'Next planned', value: row => formatInsightsDate(row.nextPlannedAt) },
        { header: 'SLA', value: row => row.slaLabel }
      ],
      serviceCaseDrilldownRows
    );
  }, [formatInsightsDate, serviceCaseDrilldownRows]);

  const exportReliabilityContributingCases = React.useCallback(() => {
    exportInsightsRowsToCsv(
      'reliability-contributing-cases.csv',
      [
        { header: 'Case', value: row => row.caseCode },
        { header: 'Customer', value: row => row.customerName },
        { header: 'Device', value: row => row.deviceLabel || row.modelLabel },
        { header: 'Status', value: row => row.statusLabel },
        { header: 'Opened', value: row => formatInsightsDate(row.openedAt) },
        { header: 'Closed', value: row => formatInsightsDate(row.closedAt) },
        { header: 'Duration days', value: row => row.durationDays },
        { header: 'Active tasks', value: row => row.activeTasks },
        { header: 'Repair tasks', value: row => row.repairTasks },
        { header: 'Task groups', value: row => row.taskGroups || [] },
        { header: 'Warranty', value: row => row.warrantyStatus === 'IN' ? 'In warranty' : 'Out of warranty' },
        { header: 'Owner', value: row => row.assigneeLabel || 'Unassigned' }
      ],
      reliabilityCaseDrilldownRows
    );
  }, [formatInsightsDate, reliabilityCaseDrilldownRows]);

  const exportCustomerContributingCases = React.useCallback(() => {
    exportInsightsRowsToCsv(
      'customer-contributing-cases.csv',
      [
        { header: 'Case', value: row => row.caseCode },
        { header: 'Customer', value: row => row.customerName },
        { header: 'Status', value: row => row.statusLabel },
        { header: 'Age days', value: row => row.ageDays },
        { header: 'Schedule', value: row => row.scheduleState },
        { header: 'Active tasks', value: row => row.activeTasks },
        { header: 'Scheduled tasks', value: row => row.scheduledTasks },
        { header: 'Task groups', value: row => row.taskGroups || [] },
        { header: 'Quote state', value: row => row.quoteState },
        { header: 'Owner', value: row => row.assigneeLabel || 'Unassigned' },
        { header: 'Opened', value: row => formatInsightsDate(row.openedAt) },
        { header: 'Closed', value: row => formatInsightsDate(row.closedAt) },
        { header: 'Last activity', value: row => formatInsightsDate(row.lastActivityAt) }
      ],
      customerCaseDrilldownRows
    );
  }, [customerCaseDrilldownRows, formatInsightsDate]);

  const exportSlaContributingCases = React.useCallback(() => {
    exportInsightsRowsToCsv(
      'sla-contributing-cases.csv',
      [
        { header: 'Case', value: row => row.caseCode },
        { header: 'Customer', value: row => row.customerName },
        { header: 'Device', value: row => row.deviceLabel || row.modelLabel },
        { header: 'SLA', value: row => row.slaLabel },
        { header: 'Status', value: row => row.statusLabel },
        { header: 'Schedule', value: row => row.scheduleState },
        { header: 'Active tasks', value: row => row.activeTasks },
        { header: 'Scheduled tasks', value: row => row.scheduledTasks },
        { header: 'Maintenance tasks', value: row => row.maintenanceTasks },
        { header: 'Repair tasks', value: row => row.repairTasks },
        { header: 'Task groups', value: row => row.taskGroups || [] },
        { header: 'Owner', value: row => row.assigneeLabel || 'Unassigned' },
        { header: 'Next planned', value: row => formatInsightsDate(row.nextPlannedAt) },
        { header: 'Opened', value: row => formatInsightsDate(row.openedAt) },
        { header: 'Closed', value: row => formatInsightsDate(row.closedAt) },
        { header: 'Last activity', value: row => formatInsightsDate(row.lastActivityAt) }
      ],
      slaCaseDrilldownRows
    );
  }, [formatInsightsDate, slaCaseDrilldownRows]);

  const exportInventoryContributingCases = React.useCallback(() => {
    exportInsightsRowsToCsv(
      'inventory-contributing-cases.csv',
      [
        { header: 'Case', value: row => row.caseCode },
        { header: 'Customer', value: row => row.customerName },
        { header: 'Device', value: row => row.deviceLabel },
        { header: 'Status', value: row => row.statusLabel },
        { header: 'Shortage quantity', value: row => row.shortageQty },
        { header: 'Parts detail', value: row => row.detail },
        { header: 'ETA', value: row => formatInsightsDate(row.etaAt) },
        { header: 'Active tasks', value: row => row.activeTasks },
        { header: 'Task groups', value: row => row.taskGroups || [] },
        { header: 'Owner', value: row => row.assigneeLabel || 'Unassigned' },
        { header: 'Plan status', value: row => row.planStatus }
      ],
      inventoryCaseDrilldownRows
    );
  }, [formatInsightsDate, inventoryCaseDrilldownRows]);

  const exportTeamContributingCases = React.useCallback(() => {
    exportInsightsRowsToCsv(
      'team-contributing-cases.csv',
      [
        { header: 'Case', value: row => row.caseCode },
        { header: 'Customer', value: row => row.customerName },
        { header: 'Device', value: row => row.deviceLabel || row.modelLabel },
        { header: 'Status', value: row => row.statusLabel },
        { header: 'Active tasks', value: row => row.activeTasks },
        { header: 'Completed tasks', value: row => row.completedTasks },
        { header: 'Scheduled tasks', value: row => row.scheduledTasks },
        { header: 'Pending schedule', value: row => row.pendingSchedule },
        { header: 'Task groups', value: row => row.taskGroups || [] },
        { header: 'Owner', value: row => row.assigneeLabel || 'Unassigned' },
        { header: 'Opened', value: row => formatInsightsDate(row.openedAt) },
        { header: 'Closed', value: row => formatInsightsDate(row.closedAt) },
        { header: 'Last activity', value: row => formatInsightsDate(row.lastActivityAt) }
      ],
      teamCaseDrilldownRows
    );
  }, [formatInsightsDate, teamCaseDrilldownRows]);

  const teamSelectableUsers = React.useMemo(() => {
    const names = new Set<string>();
    teamRepairPerformanceRows.forEach(row => {
      const technician = String(row.technician || '').trim();
      if (technician) names.add(technician);
    });
    loadByAssigneeRows.forEach(row => {
      const technician = String(row.assignee || '').trim();
      if (technician && technician !== 'Unassigned') names.add(technician);
    });
    userTaskHoursRows.forEach(row => {
      const technician = String(row.technician || '').trim();
      if (technician) names.add(technician);
    });
    scheduledUserRows.forEach(row => {
      const technician = String(row.technician || '').trim();
      if (technician) names.add(technician);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [loadByAssigneeRows, scheduledUserRows, teamRepairPerformanceRows, userTaskHoursRows]);

  const [selectedTeamUser, setSelectedTeamUser] = React.useState<string>('all');

  React.useEffect(() => {
    if (selectedTeamUser === 'all') return;
    if (!teamSelectableUsers.includes(selectedTeamUser)) {
      setSelectedTeamUser('all');
    }
  }, [selectedTeamUser, teamSelectableUsers]);

  const filteredTeamRepairPerformanceRows = React.useMemo(() => {
    if (selectedTeamUser === 'all') return teamRepairPerformanceRows;
    return teamRepairPerformanceRows.filter(row => row.technician === selectedTeamUser);
  }, [selectedTeamUser, teamRepairPerformanceRows]);

  const derivedTeamWorkloadDetailRows = React.useMemo(() => {
    const grouped = new Map<
      string,
      {
        technician: string;
        backlog: number;
        scheduled: number;
        pendingSchedule: number;
        hours: number;
      }
    >();

    loadByAssigneeRows.forEach(row => {
      const technician = String(row.assignee || '').trim();
      if (!technician || technician === 'Unassigned') return;
      const current =
        grouped.get(technician) || {
          technician,
          backlog: 0,
          scheduled: 0,
          pendingSchedule: 0,
          hours: 0
        };
      current.backlog = row.tasks;
      current.scheduled = row.scheduled;
      current.pendingSchedule = row.pendingSchedule;
      grouped.set(technician, current);
    });

    userTaskHoursRows.forEach(row => {
      const technician = String(row.technician || '').trim();
      if (!technician) return;
      const current =
        grouped.get(technician) || {
          technician,
          backlog: 0,
          scheduled: 0,
          pendingSchedule: 0,
          hours: 0
        };
      current.hours = row.totalHours;
      grouped.set(technician, current);
    });

    scheduledUserRows.forEach(row => {
      const technician = String(row.technician || '').trim();
      if (!technician) return;
      const current =
        grouped.get(technician) || {
          technician,
          backlog: 0,
          scheduled: 0,
          pendingSchedule: 0,
          hours: 0
        };
      current.scheduled = Math.max(current.scheduled, row.tasks);
      grouped.set(technician, current);
    });

    return Array.from(grouped.values()).sort(
      (a, b) =>
        b.backlog - a.backlog ||
        b.scheduled - a.scheduled ||
        b.hours - a.hours ||
        a.technician.localeCompare(b.technician)
    );
  }, [loadByAssigneeRows, scheduledUserRows, userTaskHoursRows]);

  const teamWorkloadDetailRows = React.useMemo(
    () => (serverTeamWorkloadDetailRows.length > 0 ? serverTeamWorkloadDetailRows : derivedTeamWorkloadDetailRows),
    [derivedTeamWorkloadDetailRows, serverTeamWorkloadDetailRows]
  );

  const filteredTeamWorkloadDetailRows = React.useMemo(() => {
    if (selectedTeamUser === 'all') return teamWorkloadDetailRows;
    return teamWorkloadDetailRows.filter(row => row.technician === selectedTeamUser);
  }, [selectedTeamUser, teamWorkloadDetailRows]);

  const inventoryMovementRows = React.useMemo(
    () => inventoryTransitRows.filter(row => row.quantityInTransit > 0 || row.quantityOnOrder > 0),
    [inventoryTransitRows]
  );
  const showInventoryStockValue =
    metricsWorkspaceTab === 'inventory' && isMetricsWorkspaceBlockEnabled('stock-value-by-warehouse');
  const showInventoryTransit =
    metricsWorkspaceTab === 'inventory' && isMetricsWorkspaceBlockEnabled('transit-on-order-posture');
  const showInventoryVariance =
    metricsWorkspaceTab === 'inventory' && isMetricsWorkspaceBlockEnabled('stock-variance-by-count-type');
  const showInventoryRecentCounts =
    metricsWorkspaceTab === 'inventory' && isMetricsWorkspaceBlockEnabled('recent-approved-counts');
  const showInventoryVanStock =
    metricsWorkspaceTab === 'inventory' && isMetricsWorkspaceBlockEnabled('van-stock-by-user-warehouse');
  const showInventoryDelayedParts =
    metricsWorkspaceTab === 'inventory' && isMetricsWorkspaceBlockEnabled('delayed-parts-repair-delays');
  const showInventoryCountControl = showInventoryVariance || showInventoryRecentCounts;
  const showInventoryLeftColumn = showInventoryStockValue || showInventoryTransit || showInventoryCountControl;
  const showInventoryRightColumn = showInventoryDelayedParts || showInventoryVanStock;
  const showInventoryAnyDetail = showInventoryLeftColumn || showInventoryRightColumn;
  const showTeamBacklog =
    metricsWorkspaceTab === 'team' && isMetricsWorkspaceBlockEnabled('backlog-by-user');
  const showTeamHours =
    metricsWorkspaceTab === 'team' && isMetricsWorkspaceBlockEnabled('hours-on-task-by-user');
  const showTeamSatisfaction =
    metricsWorkspaceTab === 'team' && isMetricsWorkspaceBlockEnabled('customer-satisfaction-by-user');
  const showTeamMtbf = metricsWorkspaceTab === 'team' && isMetricsWorkspaceBlockEnabled('user-mtbf');
  const showTeamFirstTimeFix =
    metricsWorkspaceTab === 'team' && isMetricsWorkspaceBlockEnabled('first-time-fix-by-user');
  const showTeamScheduledLoad =
    metricsWorkspaceTab === 'team' && isMetricsWorkspaceBlockEnabled('scheduled-load-by-user');
  const showTeamRepairPerformance =
    metricsWorkspaceTab === 'team' && isMetricsWorkspaceBlockEnabled('repair-performance');
  const showTeamWorkloadDetail =
    metricsWorkspaceTab === 'team' && isMetricsWorkspaceBlockEnabled('workload-detail');
  const showTeamContributingCases =
    metricsWorkspaceTab === 'team' && isMetricsWorkspaceBlockEnabled('contributing-cases');
  const showTeamWorkloadLane =
    showTeamBacklog || showTeamHours || showTeamScheduledLoad || showTeamWorkloadDetail;
  const showTeamQualityLane =
    showTeamSatisfaction || showTeamMtbf || showTeamFirstTimeFix || showTeamRepairPerformance;
  const showTeamRollupGrid =
    showTeamBacklog ||
    showTeamHours ||
    showTeamSatisfaction ||
    showTeamMtbf ||
    showTeamFirstTimeFix ||
    showTeamScheduledLoad;
  const showTeamDetailGrid = showTeamRepairPerformance || showTeamWorkloadDetail;
  const showTeamAnyDetail = showTeamWorkloadLane || showTeamQualityLane || showTeamContributingCases;

  return (
    <>
      {isMetricsWidgetEnabled('signals') && insightSignals.length > 0 && (
        <div style={styles.insightsSignalsGrid}>
          {insightSignals.map(signal => (
            <div key={signal.id} style={getInsightsSignalStyle(signal.tone)}>
              <div style={styles.insightsSignalTitle}>{signal.title}</div>
              <div style={styles.insightsSignalDetail}>{signal.detail}</div>
            </div>
          ))}
        </div>
      )}

      {isMetricsWidgetEnabled(`${metricsWorkspaceTab}-kpis`) && (
        <div style={styles.insightsKpiGrid}>
          {(metricsWorkspaceKpis[metricsWorkspaceTab] || []).map(card => (
            <div key={card.label} style={styles.insightsKpiCard}>
              <div style={styles.insightsKpiLabel}>{card.label}</div>
              <div style={styles.insightsKpiValue}>{card.value}</div>
              <div style={styles.insightsKpiMeta}>{card.meta}</div>
            </div>
          ))}
        </div>
      )}

      {metricsWorkspaceTab === 'overview' && isMetricsWidgetEnabled('overview-grid') && (
        <div style={styles.insightsWidgetGrid}>
            {isMetricsWorkspaceBlockEnabled('backlog-by-task-group') && (
          <div style={styles.insightsWidget}>
            <div style={styles.insightsWidgetHeader}>
              <div style={styles.insightsWidgetTitle}>Backlog by task group</div>
              <div style={styles.insightsWidgetMeta}>Current open workload mix</div>
            </div>
            {taskGroupRows.length === 0 ? (
              <div style={styles.insightsEmptyState}>No open case workload matches the current filters.</div>
            ) : (
              <div style={styles.insightsList}>
                {taskGroupRows.slice(0, 6).map(row => {
                  const total = taskGroupRows[0]?.count || 1;
                  return (
                    <div key={row.label} style={styles.insightsListRow}>
                      <div style={styles.insightsListMain}>
                        <div style={styles.insightsListTitle}>{row.label}</div>
                        <div style={styles.insightsBarTrack}>
                          <div
                            style={{
                              ...styles.insightsBarFill,
                              width: `${Math.max(14, (row.count / total) * 100)}%`
                            }}
                          />
                        </div>
                      </div>
                      <div style={styles.insightsListValue}>{row.count}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}
            {isMetricsWorkspaceBlockEnabled('open-age-buckets') && (
          <div style={styles.insightsWidget}>
            <div style={styles.insightsWidgetHeader}>
              <div style={styles.insightsWidgetTitle}>Open age buckets</div>
              <div style={styles.insightsWidgetMeta}>How old the current backlog is</div>
            </div>
            <div style={styles.insightsBucketGrid}>
              {ageBucketRows.map(bucket => (
                <div key={bucket.label} style={styles.insightsBucketCard}>
                  <div style={styles.insightsBucketValue}>{bucket.count}</div>
                  <div style={styles.insightsBucketLabel}>{bucket.label}</div>
                </div>
              ))}
            </div>
          </div>
          )}
            {isMetricsWorkspaceBlockEnabled('upcoming-work') && (
          <div style={styles.insightsWidget}>
            <div style={styles.insightsWidgetHeader}>
              <div style={styles.insightsWidgetTitle}>Upcoming work</div>
              <div style={styles.insightsWidgetMeta}>Next 7 days of planned visits in the current scope</div>
            </div>
            {upcomingWorkRows.length === 0 ? (
              <div style={styles.insightsEmptyState}>No planned visits are currently scheduled in the next 7 days.</div>
            ) : (
              <div style={styles.insightsList}>
                {upcomingWorkRows.slice(0, 6).map(row => (
                  <div key={String(row.id || `${row.caseCode}-${row.taskGroup}`)} style={styles.insightsListRow}>
                    <div style={styles.insightsListMain}>
                      <div style={styles.insightsListTitle}>{row.caseCode}</div>
                      <div style={styles.insightsListMeta}>
                        {row.customerName} | {row.assigneeLabel} | {formatInsightsDate((row as any).plannedStart || (row as any).plannedDue)}
                      </div>
                    </div>
                    <div style={styles.insightsListValue}>{row.taskGroup || 'UNASSIGNED'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}
            {isMetricsWorkspaceBlockEnabled('completed-work-by-task-type') && (
          <div style={styles.insightsWidget}>
            <div style={styles.insightsWidgetHeader}>
              <div style={styles.insightsWidgetTitle}>Completed work by task type</div>
              <div style={styles.insightsWidgetMeta}>Tasks completed in the selected date range</div>
            </div>
            {completedTaskGroupRows.length === 0 ? (
              <div style={styles.insightsEmptyState}>No completed task activity is available in the current range.</div>
            ) : (
              <div style={styles.insightsList}>
                {completedTaskGroupRows.slice(0, 6).map(row => {
                  const total = completedTaskGroupRows[0]?.count || 1;
                  return (
                    <div key={row.label} style={styles.insightsListRow}>
                      <div style={styles.insightsListMain}>
                        <div style={styles.insightsListTitle}>{row.label}</div>
                        <div style={styles.insightsBarTrack}>
                          <div
                            style={{
                              ...styles.insightsBarFill,
                              width: `${Math.max(14, (row.count / total) * 100)}%`
                            }}
                          />
                        </div>
                      </div>
                      <div style={styles.insightsListValue}>{row.count}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}
            {isMetricsWorkspaceBlockEnabled('service-load-by-focus') && (
          <div style={styles.insightsWidget}>
            <div style={styles.insightsWidgetHeader}>
              <div style={styles.insightsWidgetTitle}>
                Service load by {metricsGroupBy === 'device' ? 'serial' : 'model'}
              </div>
              <div style={styles.insightsWidgetMeta}>Cases and tasks currently driving activity</div>
            </div>
            <table style={styles.metricsTable}>
              <thead>
                <tr>
                  <th style={styles.metricsTh}>Focus</th>
                  <th style={styles.metricsTh}>Cases</th>
                  <th style={styles.metricsTh}>Tasks</th>
                </tr>
              </thead>
              <tbody>
                {(metricsData.tasksByDevice || []).slice(0, 6).map(row => (
                  <tr key={row.key}>
                    <td style={styles.metricsTd}>{row.label}</td>
                    <td style={styles.metricsTd}>{row.caseCount}</td>
                    <td style={styles.metricsTd}>{row.taskCount}</td>
                  </tr>
                ))}
                {(metricsData.tasksByDevice || []).length === 0 && (
                  <tr>
                    <td style={styles.metricsTd} colSpan={3}>
                      No grouped workload is available for the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          )}
            {isMetricsWorkspaceBlockEnabled('service-load-by-customer') && (
          <div style={styles.insightsWidget}>
            <div style={styles.insightsWidgetHeader}>
              <div style={styles.insightsWidgetTitle}>Service load by customer</div>
              <div style={styles.insightsWidgetMeta}>Customer demand concentration in the selected range</div>
            </div>
            {customerLoadRows.length === 0 ? (
              <div style={styles.insightsEmptyState}>No customer load is visible for the current filters.</div>
            ) : (
              <div style={styles.insightsList}>
                {customerLoadRows.slice(0, 6).map(row => (
                  <div key={row.id} style={styles.insightsListRow}>
                    <div style={styles.insightsListMain}>
                      <div style={styles.insightsListTitle}>{row.customer}</div>
                      <div style={styles.insightsListMeta}>
                        {row.totalCases} cases | {row.openCases} open
                      </div>
                    </div>
                    <div style={styles.insightsListValue}>{row.taskCount}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}
        </div>
      )}

      {metricsWorkspaceTab === 'service' && isMetricsWidgetEnabled('service-grid') && (
        <div style={styles.insightsWidgetGrid}>
          {isMetricsWorkspaceBlockEnabled('open-by-status') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Open by status</div>
                <div style={styles.insightsWidgetMeta}>Current flow state across cases in scope</div>
              </div>
              {statusDistributionRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>No case activity is available for the current filters.</div>
              ) : (
                <div style={styles.insightsList}>
                  {statusDistributionRows.map(row => {
                    const total = statusDistributionRows[0]?.count || 1;
                    return (
                      <div key={row.label} style={styles.insightsListRow}>
                        <div style={styles.insightsListMain}>
                          <div style={styles.insightsListTitle}>{row.label}</div>
                          <div style={styles.insightsBarTrack}>
                            <div
                              style={{
                                ...styles.insightsBarFill,
                                width: `${Math.max(14, (row.count / total) * 100)}%`
                              }}
                            />
                          </div>
                        </div>
                        <div style={styles.insightsListValue}>{row.count}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {isMetricsWorkspaceBlockEnabled('schedule-readiness') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Schedule readiness</div>
                <div style={styles.insightsWidgetMeta}>Where execution is ready versus still blocked</div>
              </div>
              <div style={styles.insightsMiniGrid}>
                <div style={styles.insightsMiniCard}>
                  <div style={styles.insightsMiniValue}>{serviceSummary?.plannedTasks ?? scheduledTaskRows.length}</div>
                  <div style={styles.insightsMiniLabel}>Planned tasks</div>
                </div>
                <div style={styles.insightsMiniCard}>
                  <div style={styles.insightsMiniValue}>{serviceSummary?.pendingSchedule ?? pendingScheduleRows.length}</div>
                  <div style={styles.insightsMiniLabel}>Pending confirmation</div>
                </div>
                <div style={styles.insightsMiniCard}>
                  <div style={styles.insightsMiniValue}>{serviceSummary?.unassignedTasks ?? unassignedTaskRows.length}</div>
                  <div style={styles.insightsMiniLabel}>Unassigned tasks</div>
                </div>
              </div>
            </div>
          )}
          {isMetricsWorkspaceBlockEnabled('quote-pipeline') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Quote pipeline</div>
                <div style={styles.insightsWidgetMeta}>Cases carrying quotes by current decision state</div>
              </div>
              {serviceInsightQuotesLoading ? (
                <div style={styles.insightsEmptyState}>Loading quote delivery state...</div>
              ) : serviceQuoteStatusRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>No quote activity is visible in the current service scope.</div>
              ) : (
                <div style={styles.insightsList}>
                  {serviceQuoteStatusRows.map(row => {
                    const total = serviceQuoteStatusRows[0]?.count || 1;
                    return (
                      <div key={row.label} style={styles.insightsListRow}>
                        <div style={styles.insightsListMain}>
                          <div style={styles.insightsListTitle}>{row.label}</div>
                          <div style={styles.insightsBarTrack}>
                            <div
                              style={{
                                ...styles.insightsBarFill,
                                width: `${Math.max(14, (row.count / total) * 100)}%`
                              }}
                            />
                          </div>
                        </div>
                        <div style={styles.insightsListValue}>{row.count}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {isMetricsWorkspaceBlockEnabled('upcoming-planned-work') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Scheduled work</div>
                <div style={styles.insightsWidgetMeta}>Scheduled visits matching the current date filter</div>
              </div>
              <table style={styles.metricsTable}>
                <thead>
                  <tr>
                    <th style={styles.metricsTh}>Case</th>
                    <th style={styles.metricsTh}>Start</th>
                    <th style={styles.metricsTh}>Owner</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduledThisWeekRows.slice(0, 6).map(row => (
                    <tr
                      key={String(row.id || `${row.caseCode}-${row.taskGroup}`)}
                      style={{ cursor: row.caseId ? 'pointer' : 'default' }}
                      onClick={() => openServiceCase(row.caseId)}
                    >
                      <td style={styles.metricsTd}>
                        <button
                          type="button"
                          style={{
                            border: 'none',
                            background: 'transparent',
                            padding: 0,
                            color: '#083f3f',
                            fontWeight: 700,
                            cursor: row.caseId ? 'pointer' : 'default'
                          }}
                          onClick={event => {
                            event.stopPropagation();
                            openServiceCase(row.caseId);
                          }}
                        >
                          {row.caseCode}
                        </button>
                      </td>
                      <td style={styles.metricsTd}>
                        {formatInsightsDate((row as any).plannedStart || (row as any).plannedDue)}
                      </td>
                      <td style={styles.metricsTd}>{row.assigneeLabel}</td>
                    </tr>
                  ))}
                  {scheduledThisWeekRows.length === 0 && (
                    <tr>
                      <td style={styles.metricsTd} colSpan={3}>
                        No scheduled work matches the current date filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {isMetricsWorkspaceBlockEnabled('jobs-waiting-on-parts') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Jobs waiting on parts</div>
                <div style={styles.insightsWidgetMeta}>Quote-backed jobs still waiting on parts availability or dispatch</div>
              </div>
              {serviceInsightQuotesLoading ? (
                <div style={styles.insightsEmptyState}>Loading parts ETA visibility...</div>
              ) : partsWaitingRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>No current jobs are flagged as waiting on parts.</div>
              ) : (
                <div style={styles.insightsList}>
                  {partsWaitingRows.slice(0, 6).map(row => (
                    <div
                      key={row.id}
                      role="button"
                      tabIndex={0}
                      style={{ ...styles.insightsListRow, cursor: 'pointer' }}
                      onClick={() => openServiceCase(row.caseId || row.id)}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openServiceCase(row.caseId || row.id);
                        }
                      }}
                    >
                      <div style={styles.insightsListMain}>
                        <div style={styles.insightsListTitle}>{row.caseCode}</div>
                        <div style={styles.insightsListMeta}>
                          {row.customerName} | {row.detail || 'Parts action still pending'}
                        </div>
                      </div>
                      <div style={styles.insightsListValue}>
                        {row.etaAt ? formatInsightsDate(row.etaAt) : row.statusLabel}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {isMetricsWorkspaceBlockEnabled('upcoming-sla-work') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Upcoming SLA work</div>
                <div style={styles.insightsWidgetMeta}>Scheduled visits tied to active SLA-covered devices</div>
              </div>
              {upcomingSlaRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>No upcoming SLA-covered work is scheduled in the next 7 days.</div>
              ) : (
                <div style={styles.insightsList}>
                  {upcomingSlaRows.slice(0, 6).map(row => (
                    <div
                      key={String(row.id || `${row.caseCode}-${row.slaLabel}`)}
                      role="button"
                      tabIndex={0}
                      style={{ ...styles.insightsListRow, cursor: row.caseId ? 'pointer' : 'default' }}
                      onClick={() => openServiceCase(row.caseId)}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openServiceCase(row.caseId);
                        }
                      }}
                    >
                      <div style={styles.insightsListMain}>
                        <div style={styles.insightsListTitle}>{row.caseCode}</div>
                        <div style={styles.insightsListMeta}>
                          {row.customerName} | {formatInsightsDate((row as any).plannedStart || (row as any).plannedDue)} | {row.assigneeLabel}
                        </div>
                      </div>
                      <div style={styles.insightsListValue}>{row.slaLabel}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {metricsWorkspaceTab === 'service' && isMetricsWorkspaceBlockEnabled('contributing-cases') && (
        <div style={{ ...styles.insightsWidget, marginTop: '1rem' }}>
          <div style={styles.insightsWidgetHeader}>
            <div>
              <div style={styles.insightsWidgetTitle}>Contributing cases</div>
              <div style={styles.insightsWidgetMeta}>
                Cases included by the current Service Delivery filter row.
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                type="button"
                style={{
                  ...exportButtonStyle,
                  opacity: serviceCaseDrilldownRows.length === 0 ? 0.5 : 1,
                  cursor: serviceCaseDrilldownRows.length === 0 ? 'not-allowed' : 'pointer'
                }}
                disabled={serviceCaseDrilldownRows.length === 0}
                onClick={exportServiceContributingCases}
              >
                Export CSV
              </button>
              <div style={{ color: '#5f6969', fontSize: '0.85rem', fontWeight: 700 }}>
                {serviceCaseDrilldownRows.length} cases
              </div>
            </div>
          </div>
          {serviceCaseDrilldownRows.length === 0 ? (
            <div style={styles.insightsEmptyState}>No contributing cases are available for the current filters.</div>
          ) : (
            <>
              <table style={styles.metricsTable}>
                <thead>
                  <tr>
                    <th style={styles.metricsTh}>Case</th>
                    <th style={styles.metricsTh}>Customer</th>
                    <th style={styles.metricsTh}>Status</th>
                    <th style={styles.metricsTh}>Schedule</th>
                    <th style={styles.metricsTh}>Quote / parts</th>
                    <th style={styles.metricsTh}>Owner</th>
                    <th style={styles.metricsTh}>Next</th>
                    <th style={styles.metricsTh}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {serviceCaseDrilldownRows.slice(0, 30).map(row => (
                    <tr
                      key={row.caseId}
                      style={{ cursor: onOpenInsightsCase ? 'pointer' : 'default' }}
                      onClick={() => openServiceCase(row.caseId)}
                    >
                      <td style={styles.metricsTd}>
                        <div style={{ fontWeight: 800, color: '#083f3f' }}>{row.caseCode}</div>
                        <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                          {row.ageDays != null ? `${row.ageDays}d open` : 'Age unavailable'}
                        </div>
                      </td>
                      <td style={styles.metricsTd}>{row.customerName}</td>
                      <td style={styles.metricsTd}>{row.statusLabel}</td>
                      <td style={styles.metricsTd}>
                        <div>{row.scheduleState}</div>
                        <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                          {row.activeTasks} active task{row.activeTasks === 1 ? '' : 's'}
                        </div>
                      </td>
                      <td style={styles.metricsTd}>
                        <div>{row.quoteState}</div>
                        <div
                          style={{
                            color: row.hasPartsIssue ? '#a63b32' : '#6b7280',
                            fontSize: '0.78rem',
                            fontWeight: row.hasPartsIssue ? 700 : 500
                          }}
                        >
                          {row.hasPartsIssue ? row.partsDetail || row.partsState : row.partsState}
                        </div>
                      </td>
                      <td style={styles.metricsTd}>{row.assigneeLabel || 'Unassigned'}</td>
                      <td style={styles.metricsTd}>{formatInsightsDate(row.nextPlannedAt)}</td>
                      <td style={styles.metricsTd}>
                        <button
                          type="button"
                          style={serviceDrilldownButtonStyle}
                          onClick={event => {
                            event.stopPropagation();
                            openServiceCase(row.caseId);
                          }}
                        >
                          Open case
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {serviceCaseDrilldownRows.length > 30 && (
                <div style={{ marginTop: '0.65rem', color: '#6b7280', fontSize: '0.85rem' }}>
                  Showing 30 of {serviceCaseDrilldownRows.length} cases. Tighten the filter row to narrow the list.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {metricsWorkspaceTab === 'reliability' && isMetricsWidgetEnabled('reliability-grid') && (
        <div style={styles.insightsWidgetGrid}>
          {isMetricsWorkspaceBlockEnabled('device-mtbf') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Device MTBF</div>
                <div style={styles.insightsWidgetMeta}>Average days between repeat repair demand by current grouping</div>
              </div>
              {deviceRepairMtbfRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>No repeat repair history is available for the current device scope.</div>
              ) : (
                <div style={styles.insightsList}>
                  {deviceRepairMtbfRows.slice(0, 8).map(row => (
                    <div key={row.key} style={styles.insightsListRow}>
                      <div style={styles.insightsListMain}>
                        <div style={styles.insightsListTitle}>{row.label}</div>
                        <div style={styles.insightsListMeta}>
                          {row.failures} repair cases | {row.deviceCount} devices
                        </div>
                      </div>
                      <div style={styles.insightsListValue}>{formatDurationDays(row.mtbfDays)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {isMetricsWorkspaceBlockEnabled('user-mtbf') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>User MTBF</div>
                <div style={styles.insightsWidgetMeta}>Average days until the next repair after a technician-owned repair</div>
              </div>
              {userRepairMtbfRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>No technician repeat-repair intervals are available yet.</div>
              ) : (
                <div style={styles.insightsList}>
                  {userRepairMtbfRows.slice(0, 8).map(row => (
                    <div key={row.technician} style={styles.insightsListRow}>
                      <div style={styles.insightsListMain}>
                        <div style={styles.insightsListTitle}>{row.technician}</div>
                        <div style={styles.insightsListMeta}>
                          {row.repeatRepairs} repeat intervals | {row.repairedCases} repair handoffs
                        </div>
                      </div>
                      <div style={styles.insightsListValue}>{formatDurationDays(row.mtbfDays)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {isMetricsWorkspaceBlockEnabled('time-to-repair') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Time to repair by {metricsGroupBy === 'device' ? 'device' : 'model'}</div>
                <div style={styles.insightsWidgetMeta}>Average close time for repair cases completed in the selected range</div>
              </div>
              {repairDurationRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>No closed repair duration data is available in the current range.</div>
              ) : (
                <div style={styles.insightsList}>
                  {repairDurationRows.slice(0, 8).map(row => (
                    <div key={row.key} style={styles.insightsListRow}>
                      <div style={styles.insightsListMain}>
                        <div style={styles.insightsListTitle}>{row.label}</div>
                        <div style={styles.insightsListMeta}>{row.cases} closed repairs</div>
                      </div>
                      <div style={styles.insightsListValue}>{formatDurationDays(row.averageDays)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {isMetricsWorkspaceBlockEnabled('hold-reasons') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>On hold reasons</div>
                <div style={styles.insightsWidgetMeta}>Current operational delays across quotes, parts, and execution readiness</div>
              </div>
              {serviceInsightQuotesLoading && reliabilityHoldReasonRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>Loading current hold reasons...</div>
              ) : reliabilityHoldReasonRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>No active delay reasons are currently flagged.</div>
              ) : (
                <div style={styles.insightsMiniGrid}>
                  {reliabilityHoldReasonRows.map(row => (
                    <div key={row.label} style={styles.insightsMiniCard}>
                      <div style={styles.insightsMiniValue}>{row.count}</div>
                      <div style={styles.insightsMiniLabel}>{row.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {isMetricsWorkspaceBlockEnabled('warranty-repairs') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Warranty repairs</div>
                <div style={styles.insightsWidgetMeta}>Repair workload split by warranty coverage at time of service</div>
              </div>
              <div style={styles.insightsMiniGrid}>
                <div style={styles.insightsMiniCard}>
                  <div style={styles.insightsMiniValue}>{repairWarrantySummary.inWarranty}</div>
                  <div style={styles.insightsMiniLabel}>In warranty</div>
                </div>
                <div style={styles.insightsMiniCard}>
                  <div style={styles.insightsMiniValue}>{repairWarrantySummary.outWarranty}</div>
                  <div style={styles.insightsMiniLabel}>Out of warranty</div>
                </div>
                <div style={styles.insightsMiniCard}>
                  <div style={styles.insightsMiniValue}>{repairWarrantySummary.total}</div>
                  <div style={styles.insightsMiniLabel}>Repair cases</div>
                </div>
              </div>
            </div>
          )}
          {isMetricsWorkspaceBlockEnabled('common-failure-modes') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Common failure modes</div>
                <div style={styles.insightsWidgetMeta}>Top TSFAD causes in the current repair scope</div>
              </div>
              {reliabilityInsightsLoading && commonFailureModeRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>Loading failure-mode patterns...</div>
              ) : commonFailureModeRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>No TSFAD failure-mode patterns are available in this range.</div>
              ) : (
                <div style={styles.insightsList}>
                  {commonFailureModeRows.map(row => (
                    <div key={row.label} style={styles.insightsListRow}>
                      <div style={styles.insightsListMain}>
                        <div style={styles.insightsListTitle}>{row.label}</div>
                      </div>
                      <div style={styles.insightsListValue}>{row.count}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {isMetricsWorkspaceBlockEnabled('common-parts-used') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Common parts used</div>
                <div style={styles.insightsWidgetMeta}>Most frequently consumed repair parts in the filtered range</div>
              </div>
              {reliabilityInsightsLoading && commonPartUsageRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>Loading repair parts usage...</div>
              ) : commonPartUsageRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>No repair part consumption is available in this range.</div>
              ) : (
                <div style={styles.insightsList}>
                  {commonPartUsageRows.map(row => (
                    <div key={row.label} style={styles.insightsListRow}>
                      <div style={styles.insightsListMain}>
                        <div style={styles.insightsListTitle}>{row.label}</div>
                        <div style={styles.insightsListMeta}>{row.count} usage events</div>
                      </div>
                      <div style={styles.insightsListValue}>{row.quantity.toFixed(0)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {isMetricsWorkspaceBlockEnabled('repair-frequency') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Repair frequency vs installed base</div>
                <div style={styles.insightsWidgetMeta}>Repair cases as a percentage of the active installed base by model</div>
              </div>
              {repairFrequencyRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>No installed-base repair frequency can be calculated in the current scope.</div>
              ) : (
                <div style={styles.insightsList}>
                  {repairFrequencyRows.slice(0, 8).map(row => (
                    <div key={row.key} style={styles.insightsListRow}>
                      <div style={styles.insightsListMain}>
                        <div style={styles.insightsListTitle}>{row.label}</div>
                        <div style={styles.insightsListMeta}>
                          {row.repairs} repairs | {row.installedBase} installed | {row.affectedDevices} affected devices
                        </div>
                      </div>
                      <div style={styles.insightsListValue}>
                        {row.percent != null ? `${Math.round(row.percent)}%` : '-'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {isMetricsWorkspaceBlockEnabled('maintenance-compliance') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Maintenance compliance</div>
                <div style={styles.insightsWidgetMeta}>Devices with maintenance completed on or before the planned due date</div>
              </div>
              <div style={styles.insightsMiniGrid}>
                <div style={styles.insightsMiniCard}>
                  <div style={styles.insightsMiniValue}>{maintenanceComplianceSummary.compliantDevices}</div>
                  <div style={styles.insightsMiniLabel}>Compliant devices</div>
                </div>
                <div style={styles.insightsMiniCard}>
                  <div style={styles.insightsMiniValue}>
                    {Math.max(0, maintenanceComplianceSummary.devicesDue - maintenanceComplianceSummary.compliantDevices)}
                  </div>
                  <div style={styles.insightsMiniLabel}>Outside target</div>
                </div>
                <div style={styles.insightsMiniCard}>
                  <div style={styles.insightsMiniValue}>{maintenanceComplianceSummary.devicesDue}</div>
                  <div style={styles.insightsMiniLabel}>Devices due</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {metricsWorkspaceTab === 'reliability' && isMetricsWorkspaceBlockEnabled('contributing-cases') && (
        <div style={{ ...styles.insightsWidget, marginTop: '1rem' }}>
          <div style={styles.insightsWidgetHeader}>
            <div>
              <div style={styles.insightsWidgetTitle}>Contributing cases</div>
              <div style={styles.insightsWidgetMeta}>
                Cases included by the current Reliability filter row.
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                type="button"
                style={{
                  ...exportButtonStyle,
                  opacity: reliabilityCaseDrilldownRows.length === 0 ? 0.5 : 1,
                  cursor: reliabilityCaseDrilldownRows.length === 0 ? 'not-allowed' : 'pointer'
                }}
                disabled={reliabilityCaseDrilldownRows.length === 0}
                onClick={exportReliabilityContributingCases}
              >
                Export CSV
              </button>
              <div style={{ color: '#5f6969', fontSize: '0.85rem', fontWeight: 700 }}>
                {reliabilityCaseDrilldownRows.length} cases
              </div>
            </div>
          </div>
          {reliabilityCaseDrilldownRows.length === 0 ? (
            <div style={styles.insightsEmptyState}>No contributing cases are available for the current filters.</div>
          ) : (
            <>
              <table style={styles.metricsTable}>
                <thead>
                  <tr>
                    <th style={styles.metricsTh}>Case</th>
                    <th style={styles.metricsTh}>Customer / device</th>
                    <th style={styles.metricsTh}>Status</th>
                    <th style={styles.metricsTh}>Repair scope</th>
                    <th style={styles.metricsTh}>Warranty</th>
                    <th style={styles.metricsTh}>Owner</th>
                    <th style={styles.metricsTh}>Timing</th>
                    <th style={styles.metricsTh}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {reliabilityCaseDrilldownRows.slice(0, 30).map(row => (
                    <tr
                      key={row.caseId}
                      style={{ cursor: onOpenInsightsCase ? 'pointer' : 'default' }}
                      onClick={() => openServiceCase(row.caseId)}
                    >
                      <td style={styles.metricsTd}>
                        <div style={{ fontWeight: 800, color: '#083f3f' }}>{row.caseCode}</div>
                        <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                          {formatInsightsDate(row.openedAt)}
                        </div>
                      </td>
                      <td style={styles.metricsTd}>
                        <div>{row.customerName}</div>
                        <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                          {row.deviceLabel || row.modelLabel || 'Device unavailable'}
                        </div>
                      </td>
                      <td style={styles.metricsTd}>{row.statusLabel}</td>
                      <td style={styles.metricsTd}>
                        <div>
                          {row.repairTasks} repair task{row.repairTasks === 1 ? '' : 's'}
                        </div>
                        <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                          {row.taskGroups?.length ? row.taskGroups.join(', ') : `${row.activeTasks} active task${row.activeTasks === 1 ? '' : 's'}`}
                        </div>
                      </td>
                      <td style={styles.metricsTd}>{row.warrantyStatus === 'IN' ? 'In warranty' : 'Out of warranty'}</td>
                      <td style={styles.metricsTd}>{row.assigneeLabel || 'Unassigned'}</td>
                      <td style={styles.metricsTd}>
                        <div>{row.durationDays != null ? formatDurationDays(row.durationDays) : '-'}</div>
                        <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                          {row.closedAt ? `Closed ${formatInsightsDate(row.closedAt)}` : 'Open'}
                        </div>
                      </td>
                      <td style={styles.metricsTd}>
                        <button
                          type="button"
                          style={serviceDrilldownButtonStyle}
                          onClick={event => {
                            event.stopPropagation();
                            openServiceCase(row.caseId);
                          }}
                        >
                          Open case
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {reliabilityCaseDrilldownRows.length > 30 && (
                <div style={{ marginTop: '0.65rem', color: '#6b7280', fontSize: '0.85rem' }}>
                  Showing 30 of {reliabilityCaseDrilldownRows.length} cases. Tighten the filter row to narrow the list.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {metricsWorkspaceTab === 'customers' && isMetricsWidgetEnabled('customers-grid') && (
        <>
        <div style={styles.insightsWidgetGrid}>
          {isMetricsWorkspaceBlockEnabled('job-status') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Job status</div>
                <div style={styles.insightsWidgetMeta}>Current case flow state across customer work</div>
              </div>
              <div style={styles.insightsList}>
                {statusDistributionRows.map(row => {
                  const total = statusDistributionRows[0]?.count || 1;
                  return (
                    <div key={row.label} style={styles.insightsListRow}>
                      <div style={styles.insightsListMain}>
                        <div style={styles.insightsListTitle}>{row.label}</div>
                        <div style={styles.insightsBarTrack}>
                          <div
                            style={{
                              ...styles.insightsBarFill,
                              width: `${Math.max(8, (row.count / total) * 100)}%`
                            }}
                          />
                        </div>
                      </div>
                      <div style={styles.insightsListValue}>{row.count}</div>
                    </div>
                  );
                })}
                {statusDistributionRows.length === 0 && (
                  <div style={styles.insightsEmptyState}>No customer jobs are visible for the current filters.</div>
                )}
              </div>
            </div>
          )}
          {isMetricsWorkspaceBlockEnabled('quote-status') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Quote status</div>
                <div style={styles.insightsWidgetMeta}>Latest quote state across customer cases</div>
              </div>
              {serviceInsightQuotesLoading ? (
                <div style={styles.insightsEmptyState}>Loading quote status...</div>
              ) : serviceQuoteStatusRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>No quote activity is visible in the current range.</div>
              ) : (
                <div style={styles.insightsList}>
                  {serviceQuoteStatusRows.map(row => {
                    const total = serviceQuoteStatusRows[0]?.count || 1;
                    return (
                      <div key={row.label} style={styles.insightsListRow}>
                        <div style={styles.insightsListMain}>
                          <div style={styles.insightsListTitle}>{row.label}</div>
                          <div style={styles.insightsBarTrack}>
                            <div
                              style={{
                                ...styles.insightsBarFill,
                                width: `${Math.max(8, (row.count / total) * 100)}%`
                              }}
                            />
                          </div>
                        </div>
                        <div style={styles.insightsListValue}>{row.count}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {isMetricsWorkspaceBlockEnabled('open-load-by-customer') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Open load by customer</div>
                <div style={styles.insightsWidgetMeta}>Current open case and active task concentration</div>
              </div>
              <div style={styles.insightsList}>
                {customerLoadRows.slice(0, 10).map(row => (
                  <div key={row.id} style={styles.insightsListRow}>
                    <div style={styles.insightsListMain}>
                      <div style={styles.insightsListTitle}>{row.customer}</div>
                      <div style={styles.insightsListMeta}>
                        {row.openCases} open cases | {row.taskCount} active tasks
                      </div>
                    </div>
                    <div style={styles.insightsListValue}>{row.totalCases}</div>
                  </div>
                ))}
                {customerLoadRows.length === 0 && (
                  <div style={styles.insightsEmptyState}>No current customer workload is visible for the current filters.</div>
                )}
              </div>
            </div>
          )}
          {isMetricsWorkspaceBlockEnabled('scheduled-jobs-by-customer') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Scheduled jobs by customer</div>
                <div style={styles.insightsWidgetMeta}>Upcoming planned work in the next 7 days</div>
              </div>
              <div style={styles.insightsList}>
                {customerScheduledRows.slice(0, 10).map(row => (
                  <div key={row.customer} style={styles.insightsListRow}>
                    <div style={styles.insightsListMain}>
                      <div style={styles.insightsListTitle}>{row.customer}</div>
                      <div style={styles.insightsListMeta}>
                        {row.cases} cases | next {formatInsightsDate(row.nextPlannedAt)}
                      </div>
                    </div>
                    <div style={styles.insightsListValue}>{row.jobs}</div>
                  </div>
                ))}
                {customerScheduledRows.length === 0 && (
                  <div style={styles.insightsEmptyState}>No scheduled customer jobs are currently planned.</div>
                )}
              </div>
            </div>
          )}
          {isMetricsWorkspaceBlockEnabled('completed-work-by-customer') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Top 10 customers by work carried out</div>
                <div style={styles.insightsWidgetMeta}>Completed task volume in the selected range</div>
              </div>
              <div style={styles.insightsList}>
                {customerCompletedWorkRows.slice(0, 10).map(row => (
                  <div key={row.customer} style={styles.insightsListRow}>
                    <div style={styles.insightsListMain}>
                      <div style={styles.insightsListTitle}>{row.customer}</div>
                      <div style={styles.insightsListMeta}>
                        {row.cases} cases completed | last {formatInsightsDate(row.latestCompletedAt)}
                      </div>
                    </div>
                    <div style={styles.insightsListValue}>{row.tasks}</div>
                  </div>
                ))}
                {customerCompletedWorkRows.length === 0 && (
                  <div style={styles.insightsEmptyState}>No completed customer work is available in the current range.</div>
                )}
              </div>
            </div>
          )}
          {isMetricsWorkspaceBlockEnabled('survey-scores-by-customer') && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Survey scores by customer</div>
                <div style={styles.insightsWidgetMeta}>Completed survey outcomes, ordered by lowest average first</div>
              </div>
              <div style={styles.insightsList}>
                {customerSurveyRows.slice(0, 10).map(row => (
                  <div key={row.customer} style={styles.insightsListRow}>
                    <div style={styles.insightsListMain}>
                      <div style={styles.insightsListTitle}>{row.customer}</div>
                      <div style={styles.insightsListMeta}>{row.surveys} completed surveys</div>
                    </div>
                    <div style={styles.insightsListValue}>
                      {row.average != null ? `${Math.round(row.average)}%` : '-'}
                    </div>
                  </div>
                ))}
                {customerSurveyRows.length === 0 && (
                  <div style={styles.insightsEmptyState}>No completed surveys are available in the current range.</div>
                )}
              </div>
            </div>
          )}
        </div>
        {isMetricsWorkspaceBlockEnabled('service-cycle-stages') && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Service cycle stages</div>
                <div style={styles.insightsWidgetMeta}>Average timing through assignment, quote, repair, and closure stages</div>
              </div>
              <div style={styles.insightsMiniGrid}>
                {customerStageTimingRows.map(row => (
                  <div key={row.key} style={styles.insightsMiniCard}>
                    <div style={styles.insightsMiniValue}>{formatDurationDays(row.averageDays)}</div>
                    <div style={styles.insightsMiniLabel}>{row.label}</div>
                    <div style={styles.insightsWidgetMeta}>{row.sampleCount} cases</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {isMetricsWorkspaceBlockEnabled('contributing-cases') && (
          <div style={{ ...styles.insightsWidget, marginTop: '1rem' }}>
            <div style={styles.insightsWidgetHeader}>
              <div>
                <div style={styles.insightsWidgetTitle}>Contributing cases</div>
                <div style={styles.insightsWidgetMeta}>
                  Cases included by the current Customers filter row.
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button
                  type="button"
                  style={{
                    ...exportButtonStyle,
                    opacity: customerCaseDrilldownRows.length === 0 ? 0.5 : 1,
                    cursor: customerCaseDrilldownRows.length === 0 ? 'not-allowed' : 'pointer'
                  }}
                  disabled={customerCaseDrilldownRows.length === 0}
                  onClick={exportCustomerContributingCases}
                >
                  Export CSV
                </button>
                <div style={{ color: '#5f6969', fontSize: '0.85rem', fontWeight: 700 }}>
                  {customerCaseDrilldownRows.length} cases
                </div>
              </div>
            </div>
            {customerCaseDrilldownRows.length === 0 ? (
              <div style={styles.insightsEmptyState}>No contributing cases are available for the current filters.</div>
            ) : (
              <>
                <table style={styles.metricsTable}>
                  <thead>
                    <tr>
                      <th style={styles.metricsTh}>Case</th>
                      <th style={styles.metricsTh}>Customer</th>
                      <th style={styles.metricsTh}>Status</th>
                      <th style={styles.metricsTh}>Activity</th>
                      <th style={styles.metricsTh}>Quote</th>
                      <th style={styles.metricsTh}>Owner</th>
                      <th style={styles.metricsTh}>Last</th>
                      <th style={styles.metricsTh}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerCaseDrilldownRows.slice(0, 30).map(row => (
                      <tr
                        key={row.caseId}
                        style={{ cursor: onOpenInsightsCase ? 'pointer' : 'default' }}
                        onClick={() => openServiceCase(row.caseId)}
                      >
                        <td style={styles.metricsTd}>
                          <div style={{ fontWeight: 800, color: '#083f3f' }}>{row.caseCode}</div>
                          <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                            {row.ageDays != null ? `${row.ageDays}d open` : formatInsightsDate(row.openedAt)}
                          </div>
                        </td>
                        <td style={styles.metricsTd}>{row.customerName}</td>
                        <td style={styles.metricsTd}>{row.statusLabel}</td>
                        <td style={styles.metricsTd}>
                          <div>{row.scheduleState}</div>
                          <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                            {row.activeTasks} active | {row.scheduledTasks} scheduled
                          </div>
                        </td>
                        <td style={styles.metricsTd}>{row.quoteState}</td>
                        <td style={styles.metricsTd}>{row.assigneeLabel || 'Unassigned'}</td>
                        <td style={styles.metricsTd}>{formatInsightsDate(row.lastActivityAt)}</td>
                        <td style={styles.metricsTd}>
                          <button
                            type="button"
                            style={serviceDrilldownButtonStyle}
                            onClick={event => {
                              event.stopPropagation();
                              openServiceCase(row.caseId);
                            }}
                          >
                            Open case
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {customerCaseDrilldownRows.length > 30 && (
                  <div style={{ marginTop: '0.65rem', color: '#6b7280', fontSize: '0.85rem' }}>
                    Showing 30 of {customerCaseDrilldownRows.length} cases. Tighten the filter row to narrow the list.
                  </div>
                )}
              </>
            )}
          </div>
        )}
        </>
      )}

      {metricsWorkspaceTab === 'sla' && isMetricsWidgetEnabled('sla-grid') && (
        <div style={styles.insightsWidgetGrid}>
            {isMetricsWorkspaceBlockEnabled('open-slas-by-focus') && (
              <div style={styles.insightsWidget}>
                <div style={styles.insightsWidgetHeader}>
                  <div style={styles.insightsWidgetTitle}>
                    Open SLAs by {metricsGroupBy === 'device' ? 'device' : 'model'}
                  </div>
                  <div style={styles.insightsWidgetMeta}>Active SLA coverage as of the selected reference date</div>
                </div>
                {slaInsightsLoading && slaCoverageRows.length === 0 ? (
                  <div style={styles.insightsEmptyState}>Loading active SLA coverage...</div>
                ) : slaCoverageRows.length === 0 ? (
                  <div style={styles.insightsEmptyState}>No active SLA coverage is visible for the current filters.</div>
                ) : (
                  <div style={styles.insightsList}>
                    {slaCoverageRows.slice(0, 8).map(row => (
                      <div key={row.key} style={styles.insightsListRow}>
                        <div style={styles.insightsListMain}>
                          <div style={styles.insightsListTitle}>{row.label}</div>
                          <div style={styles.insightsListMeta}>
                            {row.customers.size} customers | {Array.from(row.templateLabels).slice(0, 2).join(', ') || 'SLA coverage'}
                          </div>
                        </div>
                        <div style={styles.insightsListValue}>{row.devices}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {isMetricsWorkspaceBlockEnabled('sla-expiry-watchlist') && (
              <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>SLA expiry watchlist</div>
                <div style={styles.insightsWidgetMeta}>Current assignments nearing expiry within the next 90 days</div>
              </div>
              {slaInsightsLoading && expiringSlaRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>Loading SLA expiry windows...</div>
              ) : expiringSlaRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>No active SLAs are nearing expiry in the next 90 days.</div>
              ) : (
                <div style={styles.insightsList}>
                  {expiringSlaRows.slice(0, 8).map(row => (
                    <div key={row.id} style={styles.insightsListRow}>
                      <div style={styles.insightsListMain}>
                        <div style={styles.insightsListTitle}>{row.deviceLabel}</div>
                        <div style={styles.insightsListMeta}>
                          {row.customerName} | {row.slaLabel} | ends {formatInsightsDate(row.endAt)}
                        </div>
                      </div>
                      <div style={styles.insightsListValue}>{row.daysRemaining}d</div>
                    </div>
                  ))}
                </div>
              )}
              </div>
            )}
            {isMetricsWorkspaceBlockEnabled('sla-lifecycle') && (
              <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>SLA lifecycle</div>
                <div style={styles.insightsWidgetMeta}>Coverage created, renewed, and expired in the selected range</div>
              </div>
              <div style={styles.insightsMiniGrid}>
                <div style={styles.insightsMiniCard}>
                  <div style={styles.insightsMiniValue}>{slaLifecycleSummary.newCount}</div>
                  <div style={styles.insightsMiniLabel}>New SLAs</div>
                </div>
                <div style={styles.insightsMiniCard}>
                  <div style={styles.insightsMiniValue}>{slaLifecycleSummary.renewedCount}</div>
                  <div style={styles.insightsMiniLabel}>Renewed SLAs</div>
                </div>
                <div style={styles.insightsMiniCard}>
                  <div style={styles.insightsMiniValue}>{slaLifecycleSummary.expiredCount}</div>
                  <div style={styles.insightsMiniLabel}>Expired SLAs</div>
                </div>
              </div>
              </div>
            )}
            {isMetricsWorkspaceBlockEnabled('work-under-sla-by-customer') && (
              <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Work under SLA by customer</div>
                <div style={styles.insightsWidgetMeta}>Cases and jobs currently tied to covered devices in scope</div>
              </div>
              {slaCoveredCustomerWorkRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>No SLA-backed customer work is visible in the current scope.</div>
              ) : (
                <div style={styles.insightsList}>
                  {slaCoveredCustomerWorkRows.slice(0, 8).map(row => (
                    <div key={row.customer} style={styles.insightsListRow}>
                      <div style={styles.insightsListMain}>
                        <div style={styles.insightsListTitle}>{row.customer}</div>
                        <div style={styles.insightsListMeta}>
                          {row.cases} cases | {row.scheduled} scheduled jobs
                        </div>
                      </div>
                      <div style={styles.insightsListValue}>{row.tasks}</div>
                    </div>
                  ))}
                </div>
              )}
              </div>
            )}
            {isMetricsWorkspaceBlockEnabled('scheduled-sla-jobs') && (
              <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Scheduled SLA jobs</div>
                <div style={styles.insightsWidgetMeta}>Upcoming planned work on devices with active SLA coverage</div>
              </div>
              {scheduledSlaRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>No upcoming SLA-backed work is currently scheduled.</div>
              ) : (
                <div style={styles.insightsList}>
                  {scheduledSlaRows.slice(0, 8).map(row => (
                    <div key={String(row.id || `${row.caseCode}-${row.slaLabel}`)} style={styles.insightsListRow}>
                      <div style={styles.insightsListMain}>
                        <div style={styles.insightsListTitle}>{row.caseCode}</div>
                        <div style={styles.insightsListMeta}>
                          {row.customerName} | {formatInsightsDate((row as any).plannedStart || (row as any).plannedDue)} | {row.assigneeLabel}
                        </div>
                      </div>
                      <div style={styles.insightsListValue}>{row.slaLabel}</div>
                    </div>
                  ))}
                </div>
              )}
              </div>
            )}
            {isMetricsWorkspaceBlockEnabled('sla-mtbf-by-focus') && (
              <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>
                  SLA MTBF by {metricsGroupBy === 'device' ? 'device' : 'model'}
                </div>
                <div style={styles.insightsWidgetMeta}>Average repeat-repair interval for covered devices</div>
              </div>
              {slaMtbfRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>No SLA repair recurrence is available in the current range.</div>
              ) : (
                <div style={styles.insightsList}>
                  {slaMtbfRows.slice(0, 8).map(row => (
                    <div key={row.key} style={styles.insightsListRow}>
                      <div style={styles.insightsListMain}>
                        <div style={styles.insightsListTitle}>{row.label}</div>
                        <div style={styles.insightsListMeta}>
                          {row.repairs} repairs | {row.deviceCount} devices
                        </div>
                      </div>
                      <div style={styles.insightsListValue}>{formatDurationDays(row.mtbfDays)}</div>
                    </div>
                  ))}
                </div>
              )}
              </div>
            )}
            {isMetricsWorkspaceBlockEnabled('maintenance-deviation-by-month') && (
              <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Maintenance deviation by month</div>
                <div style={styles.insightsWidgetMeta}>Average schedule deviation and on-time rate for SLA maintenance work</div>
              </div>
              {slaMaintenanceDeviationRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>No SLA maintenance timing is available in the selected range.</div>
              ) : (
                <div style={styles.insightsList}>
                  {slaMaintenanceDeviationRows.slice(0, 8).map(row => (
                    <div key={row.key} style={styles.insightsListRow}>
                      <div style={styles.insightsListMain}>
                        <div style={styles.insightsListTitle}>{row.label}</div>
                        <div style={styles.insightsListMeta}>
                          {row.completed} maintenances | {row.onTimeRate != null ? `${Math.round(row.onTimeRate)}% on time` : 'No on-time rate'}
                        </div>
                      </div>
                      <div style={styles.insightsListValue}>{formatSignedDurationDays(row.averageDeviationDays)}</div>
                    </div>
                  ))}
                </div>
              )}
              </div>
            )}
            {isMetricsWorkspaceBlockEnabled('breakdowns-after-maintenance') && (
              <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Breakdowns after maintenance</div>
                <div style={styles.insightsWidgetMeta}>Repair demand that followed maintenance completion within 30 days</div>
              </div>
              {breakdownAfterMaintenanceRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>No post-maintenance breakdown pattern is visible in the current SLA scope.</div>
              ) : (
                <div style={styles.insightsList}>
                  {breakdownAfterMaintenanceRows.slice(0, 8).map(row => (
                    <div key={row.key} style={styles.insightsListRow}>
                      <div style={styles.insightsListMain}>
                        <div style={styles.insightsListTitle}>{row.label}</div>
                        <div style={styles.insightsListMeta}>
                          {row.devices} devices | avg {formatDurationDays(row.averageDaysAfterMaintenance)} after maintenance
                        </div>
                      </div>
                      <div style={styles.insightsListValue}>{row.breakdowns}</div>
                    </div>
                  ))}
                </div>
              )}
              </div>
            )}
            {isMetricsWorkspaceBlockEnabled('maintenance-compliance') && (
              <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Maintenance compliance</div>
                <div style={styles.insightsWidgetMeta}>SLA-covered devices completed on or before maintenance due date</div>
              </div>
              <div style={styles.insightsMiniGrid}>
                <div style={styles.insightsMiniCard}>
                  <div style={styles.insightsMiniValue}>{slaMaintenanceComplianceSummary.compliantDevices}</div>
                  <div style={styles.insightsMiniLabel}>Compliant devices</div>
                </div>
                <div style={styles.insightsMiniCard}>
                  <div style={styles.insightsMiniValue}>
                    {Math.max(0, slaMaintenanceComplianceSummary.devicesDue - slaMaintenanceComplianceSummary.compliantDevices)}
                  </div>
                  <div style={styles.insightsMiniLabel}>Outside target</div>
                </div>
                <div style={styles.insightsMiniCard}>
                  <div style={styles.insightsMiniValue}>{slaMaintenanceComplianceSummary.devicesDue}</div>
                  <div style={styles.insightsMiniLabel}>Devices due</div>
                </div>
                <div style={styles.insightsMiniCard}>
                  <div style={styles.insightsMiniValue}>
                    {slaFirstTimeFixSummary.rate != null ? `${Math.round(slaFirstTimeFixSummary.rate)}%` : '-'}
                  </div>
                  <div style={styles.insightsMiniLabel}>SLA first time fix</div>
                </div>
              </div>
              </div>
            )}
        </div>
      )}

      {metricsWorkspaceTab === 'sla' && isMetricsWorkspaceBlockEnabled('contributing-cases') && (
        <div style={{ ...styles.insightsWidget, marginTop: '1rem' }}>
          <div style={styles.insightsWidgetHeader}>
            <div>
              <div style={styles.insightsWidgetTitle}>Contributing cases</div>
              <div style={styles.insightsWidgetMeta}>
                Cases included by the current SLA filter row.
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                type="button"
                style={{
                  ...exportButtonStyle,
                  opacity: slaCaseDrilldownRows.length === 0 ? 0.5 : 1,
                  cursor: slaCaseDrilldownRows.length === 0 ? 'not-allowed' : 'pointer'
                }}
                disabled={slaCaseDrilldownRows.length === 0}
                onClick={exportSlaContributingCases}
              >
                Export CSV
              </button>
              <div style={{ color: '#5f6969', fontSize: '0.85rem', fontWeight: 700 }}>
                {slaCaseDrilldownRows.length} cases
              </div>
            </div>
          </div>
          {slaCaseDrilldownRows.length === 0 ? (
            <div style={styles.insightsEmptyState}>No contributing cases are available for the current filters.</div>
          ) : (
            <>
              <table style={styles.metricsTable}>
                <thead>
                  <tr>
                    <th style={styles.metricsTh}>Case</th>
                    <th style={styles.metricsTh}>Customer / device</th>
                    <th style={styles.metricsTh}>Status</th>
                    <th style={styles.metricsTh}>SLA</th>
                    <th style={styles.metricsTh}>Work</th>
                    <th style={styles.metricsTh}>Owner</th>
                    <th style={styles.metricsTh}>Next</th>
                    <th style={styles.metricsTh}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {slaCaseDrilldownRows.slice(0, 30).map(row => (
                    <tr
                      key={row.caseId}
                      style={{ cursor: onOpenInsightsCase ? 'pointer' : 'default' }}
                      onClick={() => openServiceCase(row.caseId)}
                    >
                      <td style={styles.metricsTd}>
                        <div style={{ fontWeight: 800, color: '#083f3f' }}>{row.caseCode}</div>
                        <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                          {formatInsightsDate(row.openedAt)}
                        </div>
                      </td>
                      <td style={styles.metricsTd}>
                        <div>{row.customerName}</div>
                        <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                          {row.deviceLabel || row.modelLabel || 'Device unavailable'}
                        </div>
                      </td>
                      <td style={styles.metricsTd}>{row.statusLabel}</td>
                      <td style={styles.metricsTd}>{row.slaLabel || 'SLA coverage'}</td>
                      <td style={styles.metricsTd}>
                        <div>{row.scheduleState}</div>
                        <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                          {row.activeTasks} active | {row.maintenanceTasks} maint | {row.repairTasks} repair
                        </div>
                      </td>
                      <td style={styles.metricsTd}>{row.assigneeLabel || 'Unassigned'}</td>
                      <td style={styles.metricsTd}>{formatInsightsDate(row.nextPlannedAt)}</td>
                      <td style={styles.metricsTd}>
                        <button
                          type="button"
                          style={serviceDrilldownButtonStyle}
                          onClick={event => {
                            event.stopPropagation();
                            openServiceCase(row.caseId);
                          }}
                        >
                          Open case
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {slaCaseDrilldownRows.length > 30 && (
                <div style={{ marginTop: '0.65rem', color: '#6b7280', fontSize: '0.85rem' }}>
                  Showing 30 of {slaCaseDrilldownRows.length} cases. Tighten the filter row to narrow the list.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {metricsWorkspaceTab === 'inventory' && isMetricsWidgetEnabled('inventory-grid') && (
        <div style={styles.inventoryInsightsGrid}>
          {showInventoryLeftColumn && (
            <div
              style={{
                ...styles.inventoryInsightsColumn,
                ...(!showInventoryRightColumn ? styles.inventoryPanelFull : {})
              }}
            >
              {showInventoryStockValue && (
                <div style={styles.insightsWidget}>
                  <div style={styles.insightsWidgetHeader}>
                    <div style={styles.insightsWidgetTitle}>Stock value by warehouse</div>
                    <div style={styles.insightsWidgetMeta}>Accessible warehouse posture, sorted by landed-cost value on hand</div>
                  </div>
                  {inventoryInsightsLoading && inventoryWarehouseValueRows.length === 0 ? (
                    <div style={styles.insightsEmptyState}>Loading warehouse stock value...</div>
                  ) : inventoryWarehouseValueRows.length === 0 ? (
                    <div style={styles.insightsEmptyState}>No warehouse stock value is visible for the current scope.</div>
                  ) : (
                    <div style={styles.insightsTableScroll}>
                      <table style={styles.metricsTable}>
                        <thead>
                          <tr>
                            <th style={styles.metricsTh}>Warehouse</th>
                            <th style={{ ...styles.metricsTh, ...styles.insightsTableNumber }}>SKUs</th>
                            <th style={{ ...styles.metricsTh, ...styles.insightsTableNumber }}>On hand</th>
                            <th style={{ ...styles.metricsTh, ...styles.insightsTableNumber }}>In transit</th>
                            <th style={{ ...styles.metricsTh, ...styles.insightsTableNumber }}>On order</th>
                            <th style={{ ...styles.metricsTh, ...styles.insightsTableNumber }}>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inventoryWarehouseValueRows.slice(0, 8).map(row => (
                            <tr key={row.warehouseId}>
                              <td style={styles.metricsTd}>
                                <strong>{row.warehouseName}</strong>
                              </td>
                              <td style={{ ...styles.metricsTd, ...styles.insightsTableNumber }}>{row.skuCount}</td>
                              <td style={{ ...styles.metricsTd, ...styles.insightsTableNumber }}>{row.quantityOnHand.toFixed(0)}</td>
                              <td style={{ ...styles.metricsTd, ...styles.insightsTableNumber }}>{row.quantityInTransit.toFixed(0)}</td>
                              <td style={{ ...styles.metricsTd, ...styles.insightsTableNumber }}>{row.quantityOnOrder.toFixed(0)}</td>
                              <td style={{ ...styles.metricsTd, ...styles.insightsTableNumber, fontWeight: 700 }}>
                                {formatCurrencyValue(row.onHandValue)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              {showInventoryTransit && (
                <div style={styles.insightsWidget}>
                  <div style={styles.insightsWidgetHeader}>
                    <div style={styles.insightsWidgetTitle}>Transit and on-order posture</div>
                    <div style={styles.insightsWidgetMeta}>Stock already moving or ordered into the visible warehouse network</div>
                  </div>
                  {inventoryInsightsLoading && inventoryMovementRows.length === 0 ? (
                    <div style={styles.insightsEmptyState}>Loading transit and on-order posture...</div>
                  ) : inventoryMovementRows.length === 0 ? (
                    <div style={styles.insightsEmptyState}>No in-transit or on-order stock is visible in the current scope.</div>
                  ) : (
                    <div style={styles.insightsTableScroll}>
                      <table style={styles.metricsTable}>
                        <thead>
                          <tr>
                            <th style={styles.metricsTh}>Warehouse</th>
                            <th style={{ ...styles.metricsTh, ...styles.insightsTableNumber }}>In transit</th>
                            <th style={{ ...styles.metricsTh, ...styles.insightsTableNumber }}>On order</th>
                            <th style={{ ...styles.metricsTh, ...styles.insightsTableNumber }}>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inventoryMovementRows.slice(0, 8).map(row => (
                            <tr key={row.warehouseId}>
                              <td style={styles.metricsTd}>
                                <strong>{row.warehouseName}</strong>
                              </td>
                              <td style={{ ...styles.metricsTd, ...styles.insightsTableNumber }}>{row.quantityInTransit.toFixed(0)}</td>
                              <td style={{ ...styles.metricsTd, ...styles.insightsTableNumber }}>{row.quantityOnOrder.toFixed(0)}</td>
                              <td style={{ ...styles.metricsTd, ...styles.insightsTableNumber, fontWeight: 700 }}>
                                {formatCurrencyValue(row.inTransitValue + row.onOrderValue)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              {showInventoryCountControl && (
                <div style={styles.insightsWidget}>
                  <div style={styles.insightsWidgetHeader}>
                    <div style={styles.insightsWidgetTitle}>Stock count control</div>
                    <div style={styles.insightsWidgetMeta}>Approved count variance and recent count outcomes, valued at landed cost</div>
                  </div>
                  <div style={styles.inventoryCountGrid}>
                    {showInventoryVariance && (
                      <div>
                        <div style={styles.insightsWidgetMeta}>Variance by count type</div>
                        {inventoryVarianceCategoryRows.length === 0 ? (
                          <div style={styles.insightsEmptyState}>No approved count variance is available in the selected range.</div>
                        ) : (
                          <div style={styles.insightsMiniGrid}>
                            {inventoryVarianceCategoryRows.map(row => (
                              <div key={row.label} style={styles.insightsMiniCard}>
                                <div style={styles.insightsMiniValue}>{formatCurrencyValue(row.varianceValue)}</div>
                                <div style={styles.insightsMiniLabel}>{row.label}</div>
                                <div style={styles.insightsMiniLabel}>
                                  {row.sessionCount} sessions | {row.varianceLineCount} lines
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {showInventoryRecentCounts && (
                      <div>
                        <div style={styles.insightsWidgetMeta}>Recent approved counts</div>
                        {inventoryCountSessionRows.length === 0 ? (
                          <div style={styles.insightsEmptyState}>No approved stock counts are available in the selected range.</div>
                        ) : (
                          <div style={styles.insightsTableScroll}>
                            <table style={styles.metricsTable}>
                              <thead>
                                <tr>
                                  <th style={styles.metricsTh}>Count</th>
                                  <th style={styles.metricsTh}>Warehouse</th>
                                  <th style={{ ...styles.metricsTh, ...styles.insightsTableNumber }}>Lines</th>
                                  <th style={{ ...styles.metricsTh, ...styles.insightsTableNumber }}>Variance</th>
                                </tr>
                              </thead>
                              <tbody>
                                {inventoryCountSessionRows.slice(0, 8).map(row => (
                                  <tr key={row.id}>
                                    <td style={styles.metricsTd}>
                                      <strong>{row.countType}</strong>
                                      <div style={styles.insightsListMeta}>{formatInsightsDate(row.approvedAt)}</div>
                                    </td>
                                    <td style={styles.metricsTd}>
                                      {row.warehouseName}
                                      {row.userLabel ? <div style={styles.insightsListMeta}>{row.userLabel}</div> : null}
                                    </td>
                                    <td style={{ ...styles.metricsTd, ...styles.insightsTableNumber }}>
                                      {row.varianceLineCount} / {row.lineCount}
                                    </td>
                                    <td style={{ ...styles.metricsTd, ...styles.insightsTableNumber, fontWeight: 700 }}>
                                      {formatCurrencyValue(row.varianceValue)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {showInventoryRightColumn && (
            <div style={styles.inventoryInsightsColumn}>
              {showInventoryDelayedParts && (
                <div style={styles.insightsWidget}>
                  <div style={styles.insightsWidgetHeader}>
                    <div style={styles.insightsWidgetTitle}>Repair delays from parts</div>
                    <div style={styles.insightsWidgetMeta}>Open cases waiting on missing parts or replenishment ETA</div>
                  </div>
                  {inventoryInsightsLoading && inventoryDelayedPartRows.length === 0 ? (
                    <div style={styles.insightsEmptyState}>Loading delayed repair-part demand...</div>
                  ) : inventoryDelayedPartRows.length === 0 ? (
                    <div style={styles.insightsEmptyState}>No open repair cases are currently delayed by missing parts.</div>
                  ) : (
                    <div style={styles.inventoryIssueList}>
                      {inventoryDelayedPartRows.slice(0, 6).map(row => (
                        <div key={row.id} style={styles.inventoryIssueRow}>
                          <div style={styles.insightsListMain}>
                            <div style={styles.insightsListTitle}>{row.caseCode}</div>
                            <div style={styles.insightsListMeta}>
                              {row.customerName} | {row.deviceLabel}
                            </div>
                            <div style={styles.insightsListMeta}>{row.detail}</div>
                          </div>
                          <div style={styles.inventoryIssueValue}>
                            {row.etaAt ? formatInsightsDate(row.etaAt) : `${row.shortageQty.toFixed(0)} short`}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {showInventoryVanStock && (
                <div style={styles.insightsWidget}>
                  <div style={styles.insightsWidgetHeader}>
                    <div style={styles.insightsWidgetTitle}>Van stock by user warehouse</div>
                    <div style={styles.insightsWidgetMeta}>User-held stock value across technician and warehouse-user vans</div>
                  </div>
                  {inventoryVanStockRows.length === 0 ? (
                    <div style={styles.insightsEmptyState}>No user warehouse stock is currently visible.</div>
                  ) : (
                    <div style={styles.insightsTableScroll}>
                      <table style={styles.metricsTable}>
                        <thead>
                          <tr>
                            <th style={styles.metricsTh}>User warehouse</th>
                            <th style={{ ...styles.metricsTh, ...styles.insightsTableNumber }}>SKUs</th>
                            <th style={{ ...styles.metricsTh, ...styles.insightsTableNumber }}>On hand</th>
                            <th style={{ ...styles.metricsTh, ...styles.insightsTableNumber }}>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inventoryVanStockRows.slice(0, 8).map(row => (
                            <tr key={row.warehouseId}>
                              <td style={styles.metricsTd}>
                                <strong>{row.userLabel || row.warehouseName}</strong>
                              </td>
                              <td style={{ ...styles.metricsTd, ...styles.insightsTableNumber }}>{row.skuCount}</td>
                              <td style={{ ...styles.metricsTd, ...styles.insightsTableNumber }}>{row.quantityOnHand.toFixed(0)}</td>
                              <td style={{ ...styles.metricsTd, ...styles.insightsTableNumber, fontWeight: 700 }}>
                                {formatCurrencyValue(row.onHandValue)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                  )}
                </div>
              )}
            </div>
          )}
          {!showInventoryAnyDetail && (
            <div style={{ ...styles.insightsWidget, ...styles.inventoryPanelFull }}>
              <div style={styles.insightsEmptyState}>
                No Inventory detail blocks are currently included. Use Include to add stock posture, movement, count, van-stock, or delay blocks.
              </div>
            </div>
          )}
        </div>
      )}

      {metricsWorkspaceTab === 'inventory' && isMetricsWorkspaceBlockEnabled('contributing-cases') && (
        <div style={{ ...styles.insightsWidget, marginTop: '1rem' }}>
          <div style={styles.insightsWidgetHeader}>
            <div>
              <div style={styles.insightsWidgetTitle}>Contributing cases</div>
              <div style={styles.insightsWidgetMeta}>
                Part-delayed cases included by the current Inventory filter row.
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                type="button"
                style={{
                  ...exportButtonStyle,
                  opacity: inventoryCaseDrilldownRows.length === 0 ? 0.5 : 1,
                  cursor: inventoryCaseDrilldownRows.length === 0 ? 'not-allowed' : 'pointer'
                }}
                disabled={inventoryCaseDrilldownRows.length === 0}
                onClick={exportInventoryContributingCases}
              >
                Export CSV
              </button>
              <div style={{ color: '#5f6969', fontSize: '0.85rem', fontWeight: 700 }}>
                {inventoryCaseDrilldownRows.length} cases
              </div>
            </div>
          </div>
          {inventoryCaseDrilldownRows.length === 0 ? (
            <div style={styles.insightsEmptyState}>No part-delayed contributing cases are available for the current filters.</div>
          ) : (
            <>
              <table style={styles.metricsTable}>
                <thead>
                  <tr>
                    <th style={styles.metricsTh}>Case</th>
                    <th style={styles.metricsTh}>Customer / device</th>
                    <th style={styles.metricsTh}>Status</th>
                    <th style={styles.metricsTh}>Parts</th>
                    <th style={styles.metricsTh}>Owner</th>
                    <th style={styles.metricsTh}>ETA</th>
                    <th style={styles.metricsTh}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {inventoryCaseDrilldownRows.slice(0, 30).map(row => (
                    <tr
                      key={row.caseId}
                      style={{ cursor: onOpenInsightsCase ? 'pointer' : 'default' }}
                      onClick={() => openServiceCase(row.caseId)}
                    >
                      <td style={styles.metricsTd}>
                        <div style={{ fontWeight: 800, color: '#083f3f' }}>{row.caseCode}</div>
                        <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                          {row.activeTasks} active task{row.activeTasks === 1 ? '' : 's'}
                        </div>
                      </td>
                      <td style={styles.metricsTd}>
                        <div>{row.customerName}</div>
                        <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                          {row.deviceLabel || 'Device unavailable'}
                        </div>
                      </td>
                      <td style={styles.metricsTd}>{row.statusLabel}</td>
                      <td style={styles.metricsTd}>
                        <div style={{ fontWeight: 700 }}>{formatQuantityValue(row.shortageQty)} short</div>
                        <div style={{ color: '#a63b32', fontSize: '0.78rem', fontWeight: 700 }}>
                          {row.detail}
                        </div>
                      </td>
                      <td style={styles.metricsTd}>{row.assigneeLabel || 'Unassigned'}</td>
                      <td style={styles.metricsTd}>{formatInsightsDate(row.etaAt)}</td>
                      <td style={styles.metricsTd}>
                        <button
                          type="button"
                          style={serviceDrilldownButtonStyle}
                          onClick={event => {
                            event.stopPropagation();
                            openServiceCase(row.caseId);
                          }}
                        >
                          Open case
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {inventoryCaseDrilldownRows.length > 30 && (
                <div style={{ marginTop: '0.65rem', color: '#6b7280', fontSize: '0.85rem' }}>
                  Showing 30 of {inventoryCaseDrilldownRows.length} cases. Tighten the filter row to narrow the list.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {metricsWorkspaceTab === 'team' && isMetricsWidgetEnabled('team-grid') && (
        <>
          {showTeamRollupGrid && (
          <div style={styles.teamInsightsGrid}>
            {showTeamBacklog && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Backlog by user</div>
                <div style={styles.insightsWidgetMeta}>Who currently carries the active task load</div>
              </div>
              <div style={styles.insightsList}>
                {loadByAssigneeRows.slice(0, 10).map(row => (
                  <div key={row.assignee} style={styles.insightsListRow}>
                    <div style={styles.insightsListMain}>
                      <div style={styles.insightsListTitle}>{row.assignee}</div>
                      <div style={styles.insightsListMeta}>
                        {row.scheduled} scheduled | {row.pendingSchedule} pending schedule
                      </div>
                    </div>
                    <div style={styles.insightsListValue}>{row.tasks}</div>
                  </div>
                ))}
                {loadByAssigneeRows.length === 0 && (
                  <div style={styles.insightsEmptyState}>No active task ownership is visible for the current scope.</div>
                )}
              </div>
            </div>
            )}
            {showTeamHours && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Hours on task by user</div>
                <div style={styles.insightsWidgetMeta}>Tracked task duration from start to completion</div>
              </div>
              <div style={styles.insightsList}>
                {userTaskHoursRows.slice(0, 10).map(row => (
                  <div key={row.technician} style={styles.insightsListRow}>
                    <div style={styles.insightsListMain}>
                      <div style={styles.insightsListTitle}>{row.technician}</div>
                      <div style={styles.insightsListMeta}>
                        {row.taskCount} completed tasks | avg {formatHours(row.averageHours)}
                      </div>
                    </div>
                    <div style={styles.insightsListValue}>{formatHours(row.totalHours)}</div>
                  </div>
                ))}
                {userTaskHoursRows.length === 0 && (
                  <div style={styles.insightsEmptyState}>No completed task durations are available in this range.</div>
                )}
              </div>
            </div>
            )}
            {showTeamSatisfaction && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Customer satisfaction by user</div>
                <div style={styles.insightsWidgetMeta}>Completed survey outcomes by named technician</div>
              </div>
              <div style={styles.insightsList}>
                {surveyByTechnicianRows.slice(0, 10).map(row => (
                  <div key={row.technician} style={styles.insightsListRow}>
                    <div style={styles.insightsListMain}>
                      <div style={styles.insightsListTitle}>{row.technician}</div>
                      <div style={styles.insightsListMeta}>{row.surveys} completed surveys</div>
                    </div>
                    <div style={styles.insightsListValue}>
                      {row.average != null ? `${Math.round(row.average)}%` : '-'}
                    </div>
                  </div>
                ))}
                {surveyByTechnicianRows.length === 0 && (
                  <div style={styles.insightsEmptyState}>No technician survey outcomes are available in this range.</div>
                )}
              </div>
            </div>
            )}
            {showTeamMtbf && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>User MTBF</div>
                <div style={styles.insightsWidgetMeta}>Average repair-return interval by technician</div>
              </div>
              <div style={styles.insightsList}>
                {userRepairMtbfRows.slice(0, 10).map(row => (
                  <div key={row.technician} style={styles.insightsListRow}>
                    <div style={styles.insightsListMain}>
                      <div style={styles.insightsListTitle}>{row.technician}</div>
                      <div style={styles.insightsListMeta}>{row.repeatRepairs} repeat repair intervals</div>
                    </div>
                    <div style={styles.insightsListValue}>{formatDurationDays(row.mtbfDays)}</div>
                  </div>
                ))}
                {userRepairMtbfRows.length === 0 && (
                  <div style={styles.insightsEmptyState}>No technician MTBF data is available in this range.</div>
                )}
              </div>
            </div>
            )}
            {showTeamFirstTimeFix && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>First time fix by user</div>
                <div style={styles.insightsWidgetMeta}>Closed repairs with no repeat within 30 days</div>
              </div>
              <div style={styles.insightsList}>
                {userFirstTimeFixRows.slice(0, 10).map(row => (
                  <div key={row.technician} style={styles.insightsListRow}>
                    <div style={styles.insightsListMain}>
                      <div style={styles.insightsListTitle}>{row.technician}</div>
                      <div style={styles.insightsListMeta}>
                        {row.fixed} of {row.eligible} eligible repair closures
                      </div>
                    </div>
                    <div style={styles.insightsListValue}>
                      {row.rate != null ? `${Math.round(row.rate)}%` : '-'}
                    </div>
                  </div>
                ))}
                {userFirstTimeFixRows.length === 0 && (
                  <div style={styles.insightsEmptyState}>No technician first-time-fix data is available in this range.</div>
                )}
              </div>
            </div>
            )}
            {showTeamScheduledLoad && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>Scheduled load by user</div>
                <div style={styles.insightsWidgetMeta}>Upcoming scheduled work in the next 7 days</div>
              </div>
              <div style={styles.insightsList}>
                {scheduledUserRows.slice(0, 10).map(row => (
                  <div key={row.technician} style={styles.insightsListRow}>
                    <div style={styles.insightsListMain}>
                      <div style={styles.insightsListTitle}>{row.technician}</div>
                      <div style={styles.insightsListMeta}>
                        {row.customers} customers | next {formatInsightsDate(row.nextPlannedAt)}
                      </div>
                    </div>
                    <div style={styles.insightsListValue}>{row.tasks}</div>
                  </div>
                ))}
                {scheduledUserRows.length === 0 && (
                  <div style={styles.insightsEmptyState}>No scheduled user workload is currently planned in the next 7 days.</div>
                )}
              </div>
            </div>
            )}
          </div>
          )}
          {showTeamDetailGrid && (
          <div style={styles.teamInsightsWideGrid}>
            {showTeamRepairPerformance && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    flexWrap: 'wrap'
                  }}
                >
                  <div style={{ display: 'grid', gap: '0.2rem' }}>
                    <div style={styles.insightsWidgetTitle}>
                      Repair performance by user and {metricsGroupBy === 'device' ? 'device' : 'model'}
                    </div>
                    <div style={styles.insightsWidgetMeta}>
                      MTBF and first-time-fix view for the selected technician against the current grouping
                    </div>
                  </div>
                  <select
                    style={{ ...styles.filterSelect, minWidth: '180px' }}
                    value={selectedTeamUser}
                    onChange={event => setSelectedTeamUser(event.target.value)}
                  >
                    <option value="all">All users</option>
                    {teamSelectableUsers.map(userName => (
                      <option key={userName} value={userName}>
                        {userName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <table style={styles.metricsTable}>
                <thead>
                  <tr>
                    <th style={styles.metricsTh}>User</th>
                    <th style={styles.metricsTh}>{metricsGroupBy === 'device' ? 'Device' : 'Model'}</th>
                    <th style={styles.metricsTh}>Repairs</th>
                    <th style={styles.metricsTh}>MTBF</th>
                    <th style={styles.metricsTh}>First time fix</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTeamRepairPerformanceRows.slice(0, 12).map(row => (
                    <tr key={`${row.technician}-${row.focusLabel}`}>
                      <td style={styles.metricsTd}>{row.technician}</td>
                      <td style={styles.metricsTd}>{row.focusLabel}</td>
                      <td style={styles.metricsTd}>{row.repairs}</td>
                      <td style={styles.metricsTd}>{formatDurationDays(row.mtbfDays)}</td>
                      <td style={styles.metricsTd}>
                        {row.firstTimeFixRate != null ? `${Math.round(row.firstTimeFixRate)}%` : '-'}
                      </td>
                    </tr>
                  ))}
                  {filteredTeamRepairPerformanceRows.length === 0 && (
                    <tr>
                      <td style={styles.metricsTd} colSpan={5}>
                        No repair performance is visible for the current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            )}
            {showTeamWorkloadDetail && (
            <div style={styles.insightsWidget}>
              <div style={styles.insightsWidgetHeader}>
                <div style={styles.insightsWidgetTitle}>User workload detail</div>
                <div style={styles.insightsWidgetMeta}>Backlog, scheduled work, and tracked task hours by user</div>
              </div>
              <table style={styles.metricsTable}>
                <thead>
                  <tr>
                    <th style={styles.metricsTh}>User</th>
                    <th style={styles.metricsTh}>Backlog</th>
                    <th style={styles.metricsTh}>Scheduled</th>
                    <th style={styles.metricsTh}>Pending</th>
                    <th style={styles.metricsTh}>Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTeamWorkloadDetailRows.slice(0, 12).map(row => (
                    <tr key={row.technician}>
                      <td style={styles.metricsTd}>{row.technician}</td>
                      <td style={styles.metricsTd}>{row.backlog}</td>
                      <td style={styles.metricsTd}>{row.scheduled}</td>
                      <td style={styles.metricsTd}>{row.pendingSchedule}</td>
                      <td style={styles.metricsTd}>{formatHours(row.hours)}</td>
                    </tr>
                  ))}
                  {filteredTeamWorkloadDetailRows.length === 0 && (
                    <tr>
                      <td style={styles.metricsTd} colSpan={5}>
                        No user workload detail is visible for the current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            )}
          </div>
          )}
          {showTeamContributingCases && (
            <div style={{ ...styles.insightsWidget, ...styles.teamPanelFull }}>
              <div style={styles.insightsWidgetHeader}>
                <div>
                  <div style={styles.insightsWidgetTitle}>Contributing cases</div>
                  <div style={styles.insightsWidgetMeta}>Cases included by the current Team filter row.</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <button
                    type="button"
                    style={{
                      ...exportButtonStyle,
                      opacity: teamCaseDrilldownRows.length === 0 ? 0.5 : 1,
                      cursor: teamCaseDrilldownRows.length === 0 ? 'not-allowed' : 'pointer'
                    }}
                    disabled={teamCaseDrilldownRows.length === 0}
                    onClick={exportTeamContributingCases}
                  >
                    Export CSV
                  </button>
                  <div style={{ color: '#5f6969', fontSize: '0.85rem', fontWeight: 700 }}>
                    {teamCaseDrilldownRows.length} cases
                  </div>
                </div>
              </div>
              {teamCaseDrilldownRows.length === 0 ? (
                <div style={styles.insightsEmptyState}>No Team contributing cases match the current filter row.</div>
              ) : (
                <>
                  <table style={styles.metricsTable}>
                    <thead>
                      <tr>
                        <th style={styles.metricsTh}>Case</th>
                        <th style={styles.metricsTh}>Customer / device</th>
                        <th style={styles.metricsTh}>Status</th>
                        <th style={styles.metricsTh}>Workload</th>
                        <th style={styles.metricsTh}>Owner</th>
                        <th style={styles.metricsTh}>Timing</th>
                        <th style={styles.metricsTh}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamCaseDrilldownRows.slice(0, 30).map(row => (
                        <tr
                          key={row.caseId}
                          style={{ cursor: onOpenInsightsCase ? 'pointer' : 'default' }}
                          onClick={() => openServiceCase(row.caseId)}
                        >
                          <td style={styles.metricsTd}>
                            <div style={{ fontWeight: 800, color: '#083f3f' }}>{row.caseCode}</div>
                            <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                              {formatInsightsDate(row.openedAt)}
                            </div>
                          </td>
                          <td style={styles.metricsTd}>
                            <div>{row.customerName}</div>
                            <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                              {row.deviceLabel || row.modelLabel || 'Device unavailable'}
                            </div>
                          </td>
                          <td style={styles.metricsTd}>{row.statusLabel}</td>
                          <td style={styles.metricsTd}>
                            <div>
                              {row.activeTasks} active | {row.completedTasks} completed
                            </div>
                            <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                              {row.scheduledTasks} scheduled | {row.pendingSchedule} pending
                            </div>
                          </td>
                          <td style={styles.metricsTd}>
                            <div>{row.assigneeLabel || 'Unassigned'}</div>
                            <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                              {(row.taskGroups || []).join(', ') || 'No task groups'}
                            </div>
                          </td>
                          <td style={styles.metricsTd}>
                            <div>{row.lastActivityAt ? `Last ${formatInsightsDate(row.lastActivityAt)}` : '-'}</div>
                            <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                              {row.closedAt ? `Closed ${formatInsightsDate(row.closedAt)}` : 'Open'}
                            </div>
                          </td>
                          <td style={styles.metricsTd}>
                            <button
                              type="button"
                              style={serviceDrilldownButtonStyle}
                              onClick={event => {
                                event.stopPropagation();
                                openServiceCase(row.caseId);
                              }}
                            >
                              Open case
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {teamCaseDrilldownRows.length > 30 && (
                    <div style={{ marginTop: '0.65rem', color: '#6b7280', fontSize: '0.85rem' }}>
                      Showing 30 of {teamCaseDrilldownRows.length} cases. Tighten the filter row to narrow the list.
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {!showTeamAnyDetail && (
            <div style={{ ...styles.insightsWidget, ...styles.teamPanelFull }}>
              <div style={styles.insightsEmptyState}>
                No Team detail blocks are currently included. Use Include to add workload, quality, schedule, repair performance, or contributing-case blocks.
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
});
